import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AudioFolderData,
    AutoSaveStatus,
    ChangedDataKeys,
    DirtyTrackChange,
    IAudioState,
    ILang,
    ISubtitlesDataAll,
    ITracksAll,
    MetadataToSubtitlesLanguages,
    wireLang,
    AudioTrack,
    LocalStorageKeys,
    ICaptionSizeState,
    ISpace,
    AudioTrackData,
    SelectedTranslatedTrack,
    IStageStatus,
    FocusCueRequest,
} from "./types";
import { Change, IVoiceType } from "types";
import {
    getFoldersAction,
    getAllSubtitlesAction,
    editRusSubtitleAction,
    getDownloadedRusAudioUrl,
    splitRusSubtitleAction,
    deleteRusSubtitleAction,
    translateSubtitleAction,
    syntAudioAction,
    editTranslatedSubtitleAction,
    deleteTranslatedSubtitleAction,
    mergeSubtitles,
    deleteAudioAction,
    getSubtitleRecommendsAction,
    createRusSubIntervalAction,
    createRusSubIntervalMultiAction,
    cutAudioWithoutPauseAction,
    editMassTranslatedSubtitleAction,
    splitTranslatedSubtitleAction,
    opusSplitTranslatedSubtitleAction,
    mergeTranslatedSubtitlesAction,
    editMasterRuSubtitleAction,
    recreateTranslatedFromRuAction,
    ruDoneGetAction,
    ruDoneSetAction,
    translationDoneGetAction,
    translationDoneSetAction,
} from "./actions";
import {
    applyRuCorrectionApi,
    getProjectDataApi,
    getRealtimeEditorStatusApi,
    processVoiceoverV2FragmentApi,
    RealtimeEditorStatus,
} from "../../api/fastApi";
import { resolveFastApiForMedia } from "../../api";
import {
    EditSubtitleArgs,
    EditTranslatedSubtitle,
    EditTranslatedSubtitleArgs,
    SplitSubtitleArgs,
} from "../../api/fastApi/types";
import { IAudioHandles, IEditSubtitleArg, TranslateSubtitleRequest } from "./handleTypes";
import { mergedTargetFor, pairLangKeys } from "./mergeResponse";
import { describeSplit, SplitReport } from "./splitReport";
import {
    prepareDataOne,
    getFromLocal,
    getPlayerFromLocal,
    getPrevTrackId,
    getCaptionSizeFromLocal,
    getSpaces,
    getSpacesForAllPages,
    patchTrackInSubtitlesData,
    patchMultipleTracksInSubtitlesData,
    addMissingTracksToSubtitlesData,
    markPhrasePieces,
    phrasePieceDeleteQuestion,
    phrasePieceMergeQuestion,
    phrasePieceOf,
} from "./helpers";
import { adjustAllFragments } from "./adjustAllFragments";
import { cascadeOverlaps, ShiftPatch } from "./overlapCascade";
import {
    markOrphanVoicings,
    markOrphanVoicingsInFolders,
    orphanDeleteQuestion,
    orphanDeleteRefusal,
    removeOrphanVoicing,
} from "./orphanVoicings";
import { markManualOverlaps } from "./manualOverlaps";
import { dirtyKeyOf, movesPlace } from "./dirtyKeys";
import { overlapPushNotice as overlapPushNoticeText, serverPushDeltaMs, serverPushesOf, serverPushPatches } from "./serverPushes";
import type { OverlapPush } from "../../api/fastApi/types";
import { ManualOnlyControl, ManualOnlyFlagSource, manualOnlyFlagOfFolders, useManualOnly } from "./useManualOnly";
import { mergeTrackArrays } from "./mergeTrackArrays";
import { hasUnsavedChange, mergeSubtitleFoldersAdditive, mergeSubtitleFoldersLive } from "./liveRefreshMerge";
import { liveShiftFields, liveTimingFields, liveTimingWire } from "./liveTiming";
import { voicingCheckFailure, voicingCheckOf } from "./voicingCheck";
import { cueSpeakerOf } from "./cueSpeaker";
import { useSubtitleRequest } from "./useSubtitleRequest";
import { timelineDuration, useOriginalAudioDuration } from "./timelineDuration";
import { mockEn, mockRu } from "./mockSubtitles";
import {
    cueStartMs,
    formatCueTime,
    formatWindowTime,
    massEditFailures,
    saveFailureMessage,
    saveRefusalOf,
    saveRefusedError,
    timingRefusal,
} from "./editorErrors";
import {
    cueTimingBroken,
    cueWindow,
    pairTimingOf,
    pairTimingProblem,
    pairWindowBroken,
    restoreWindow,
    synthTimingRefusal,
    timeMs,
} from "./cueTiming";
import { errorHandler } from "../../utils/errorHandle";
import { getUiLang } from "../../utils/uiLanguage";

const AudioContextState = React.createContext<IAudioState>({} as IAudioState);
const AudioContextHandles = React.createContext<IAudioHandles>({} as IAudioHandles);

const appLangToApiLang: Record<string, string[]> = {
    en: ["eng"],
    ru: ["rus"],
    ch: ["ch", "zh"],
    zh: ["zh", "ch"],
    es: ["es"],
    de: ["de"],
    tr: ["tr"],
    ar: ["ar"],
    lit: ["lit"],
};

const taskKeys = [
    "auto_translation",
    "translation_proofreading",
    "voicing",
    "voicing_proofreading",
    "video",
    "full_video",
] as const;

const taskLabels: Record<string, string> = {
    auto_translation: "Перевод",
    translation_proofreading: "Проверка перевода",
    voicing: "Озвучка",
    voicing_proofreading: "Проверка озвучки",
    video: "Видео",
    full_video: "Полное видео",
};

function computeLectureStatus(data: any, currentLang: string): IStageStatus[] {
    if (!data?.parts?.length) return [];

    const apiLangs = appLangToApiLang[currentLang] || [currentLang];
    const totalParts = data.parts.length;

    const stages: IStageStatus[] = [];

    // Субтитры — считаем части, у которых есть языковая запись для текущего языка
    let subtitlesDone = 0;
    for (const part of data.parts) {
        if (!part.languages) continue;
        const langData = part.languages.find((l: any) => apiLangs.includes(l.id));
        if (langData) subtitlesDone++;
    }
    stages.push({
        key: "subtitles",
        label: "Субтитры",
        done: subtitlesDone,
        inProgress: 0,
        total: totalParts,
    });

    // Остальные этапы
    for (const key of taskKeys) {
        let done = 0;
        let inProgress = 0;
        let total = 0;

        for (const part of data.parts) {
            if (!part.languages) continue;
            const langData = part.languages.find((l: any) => apiLangs.includes(l.id));
            if (!langData) continue;

            const task = langData[key];
            if (!task) continue;

            total++;
            if (task.status === "done") done++;
            else if (task.status === "in_progress") inProgress++;
        }

        if (total > 0 || done > 0) {
            stages.push({
                key,
                label: taskLabels[key] || key,
                done,
                inProgress,
                total,
            });
        }
    }

    return stages;
}

const debug = false;
const offline = false;

const initialDataState: Partial<ITracksAll> = {};

/** Что испорчено в окне перевода (отрицательные место или слот) — словами, как у сервера. */
const pairWindowProblem = (target: AudioTrackData): string | null => {
    switch (synthTimingRefusal(pairTimingOf(target), "tts")) {
        case "start":
            return `начало ${String(target.start)}`;
        case "slot_start":
            return `начало слота ${String(target.start_rus_milli)}`;
        case "slot_end":
            return `конец ${String(target.end_rus_milli ?? target.end_rus)}`;
        default:
            return null;
    }
};

/**
 * Пара перевода на строке оригинала. Время — так, как его прочтёт сервер озвучки
 * (pairTimingOf): по нему ♪ и 🔊 заранее говорят, откажет ли сервер, и только тогда. Метка
 * «⚠ время» — по метке сервера (timing_invalid, его слова — timing_problem) или по тому же
 * правилу (старый сервер не метит). Испорченное ОКНО (отрицательные место или слот — конец
 * −153841 у реплики fdcde2a1) чинит «вернуть окно»; длина ≤ 0 при целом окне — длина дубля,
 * её чинит переозвучка, а раскладка, где озвучка начинается позже русского конца, — законна.
 */
const pairRowFields = (target: AudioTrackData) => {
    const timing = pairTimingOf(target);
    const problem = target.timing_problem || pairTimingProblem(target) || pairWindowProblem(target);
    // Итог проверки дубля на слух (json реплики): ♪ строки предупреждает, если слова не совпали.
    const voicingCheck = voicingCheckOf(target.voicing_check);
    return {
        translation_timing_invalid: target.timing_invalid === true || Boolean(problem),
        ...(problem ? { translation_timing_problem: problem } : {}),
        translation_window_broken: pairWindowBroken(timing),
        translation_start: timing.start,
        translation_slot_end: timing.slotEnd,
        ...(timing.slotStart !== undefined ? { translation_slot_start: timing.slotStart } : {}),
        ...(voicingCheck ? { translation_voicing_check: voicingCheck } : {}),
        // Снята ли реплика с озвучки и есть ли у неё дубль: снятая говорит это словами (01.10.2026,
        // fdcde2a1, 2:10 — перевод просто краснел), а ⊘ спрашивает, прежде чем снять озвученную.
        translation_skip: target.skip === true,
        translation_voiced: Boolean(target.file),
        // Кто говорит перевод (проба P50m10d, E6: ♪ попал в реплику зала вместо лектора — голос строки не был виден).
        translation_speaker: cueSpeakerOf(target),
        // Новый сервер прячет текст снятой реплики в `skipped_captions` (`captions: ""`): ♪ вернёт
        // её с этим текстом — значит, ♪ не заперт «пустым текстом», а метка называет этот текст.
        ...(target.skip === true && typeof target.skipped_captions === "string" && target.skipped_captions.trim()
            ? { translation_skipped_text: target.skipped_captions }
            : {}),
    };
};

/**
 * Снята ли реплика с озвучки после ♪ — по ответу сервера: это вся запись реплики после синтеза.
 * Новый сервер на ♪ возвращает снятую реплику в озвучку (`skip: false` или нет поля), и строка
 * перестаёт говорить «снята с озвучки» сразу, без перечитывания папки; текст, который он достал
 * из `skipped_captions` и озвучил, — на экран (только у реплики, что была снята: у остальных текст
 * редактора не подменяется ответом). Ответ со `skip: true` (сервер метку оставил) — метка остаётся.
 */
const skipAfterVoicing = (
    answer: { skip?: unknown; captions?: unknown },
    wasSkipped: boolean,
): Partial<Pick<AudioTrackData, "skip" | "skipped_captions" | "captions">> => {
    if (answer.skip === true) {
        return { skip: true };
    }
    if (!wasSkipped) {
        return { skip: undefined };
    }
    return {
        skip: undefined,
        skipped_captions: undefined,
        ...(typeof answer.captions === "string" ? { captions: answer.captions } : {}),
    };
};

const mockDataState: Partial<ISubtitlesDataAll> = {};

const AudioProvider = ({ children }: { children: ReactNode }) => {
    const [mp3, setMp3] = useState<string | undefined>();
    const [mediaId, setMediaId] = useState<string>("");
    const [subtitlesDataAll, setSubtitlesDataAll] =
        useState<Partial<ISubtitlesDataAll>>(mockDataState);
    const [translationTrackAll, setTranslationTrackAll] =
        useState<Partial<ITracksAll>>(initialDataState);
    const [updatedTranslation, setUpdatedTranslation] = useState(false);
    const [zoomFragment, setZoomFragment] = useState([0, 100]);
    const [scrubberWidth, setScrubberWidth] = useState(1000);
    const [playStart, setPlayStart] = useState<number | undefined>();
    const [playOriginalStart, setPlayOriginalStart] = useState(1000);
    const [startPlaying, setStartPlaying] = useState<boolean>(false);

    const [playing, setPlaying] = useState(false);
    // Mirrors `playing` for callbacks that would otherwise close over a stale
    // value, and parks a reload that arrived at a bad moment.
    const playingRef = useRef(false);
    const pendingRealtimeReloadRef = useRef(false);
    const pendingMp3Ref = useRef<string | null>(null);
    // Плеим озвучку + видео без оригинального аудио (клик по правой колонке);
    // оригинальный wavesurfer продолжает играть беззвучно как мастер-часы
    const [voiceoverOnlyPlayback, setVoiceoverOnlyPlayback] = useState(false);
    const [playbackTime, setPlaybackTime] = useState(0);
    const [activeSegmentLength, setActiveSegmentLength] = useState(10);
    //The length of the active segment in PERCENTAGE of the total audio length

    const [name, setName] = useState("");
    const [lectureName, setLectureName] = useState("");
    const [lectureStatus, setLectureStatus] = useState<IStageStatus[]>([]);
    const [useCheckedVoiceoverSynthesis, setUseCheckedVoiceoverSynthesis] = useState(() => {
        const saved = localStorage.getItem(LocalStorageKeys.useCheckedVoiceoverSynthesis);
        return saved === "1";
    });
    // Whether clicking a voicing silences the lecturer. This used to be reachable only by
    // holding Ctrl/Cmd at the moment of the click, which is invisible and has to be
    // rediscovered every session; as a setting it can be seen, and it survives a reload.
    // Default true keeps the behaviour every translator already has.
    const [muteOriginalOnVoicingClick, setMuteOriginalOnVoicingClickState] = useState(() => {
        const saved = localStorage.getItem(LocalStorageKeys.muteOriginalOnVoicingClick);
        return saved === null ? true : saved === "1";
    });
    const setMuteOriginalOnVoicingClick = useCallback((value: boolean) => {
        setMuteOriginalOnVoicingClickState(value);
        try {
            localStorage.setItem(LocalStorageKeys.muteOriginalOnVoicingClick, value ? "1" : "0");
        } catch {
            /* private mode: the choice simply does not survive the tab */
        }
        // Apply at once, so the switch is audible on the cue already playing instead of only
        // on the next click.
        setVoiceoverOnlyPlayback(value);
    }, []);
    // Второй показатель времени — от начала всего видео. По умолчанию ВКЛЮЧЁН: выключенным по
    // умолчанию его бы просто не нашли, и просьба осталась бы невыполненной. Выключатель нужен
    // потому, что вторая строка отнимает вертикальный воздух на плотных участках.
    const [showGlobalTime, setShowGlobalTimeState] = useState(() => {
        const saved = localStorage.getItem(LocalStorageKeys.showGlobalTime);
        return saved === null ? true : saved === "1";
    });
    const setShowGlobalTime = useCallback((value: boolean) => {
        setShowGlobalTimeState(value);
        try {
            localStorage.setItem(LocalStorageKeys.showGlobalTime, value ? "1" : "0");
        } catch {
            /* private mode: the choice simply does not survive the tab */
        }
    }, []);
    // Лента едет за воспроизведением. По умолчанию ВКЛЮЧЕНО: об этом и просили, а выключенную
    // по умолчанию помощь никто не найдёт. Выключатель нужен тому, кто слушает и одновременно
    // разглядывает другое место папки.
    const [followPlayback, setFollowPlaybackState] = useState(() => {
        const saved = localStorage.getItem(LocalStorageKeys.followPlayback);
        return saved === null ? true : saved === "1";
    });
    const setFollowPlayback = useCallback((value: boolean) => {
        setFollowPlaybackState(value);
        try {
            localStorage.setItem(LocalStorageKeys.followPlayback, value ? "1" : "0");
        } catch {
            /* приватный режим: выбор не переживёт вкладку */
        }
    }, []);
    const [projectData, setProjectData] = useState<any>(null);
    const [realtimeEditorStatus, setRealtimeEditorStatus] = useState<RealtimeEditorStatus | null>(null);
    const [fastApiReady, setFastApiReady] = useState(false);
    const [fastApiMediaId, setFastApiMediaId] = useState("");
    const [isStreamEditor, setIsStreamEditor] = useState(false);
    const [lang, setLang] = useState<ILang>("ru");
    const [time, setTime] = useState<number>(0);
    const [originalLang, setOriginalLang] = useState<ILang>("ru");
    const [toLang, setToLang] = useState("english");
    const [urlLang, setUrlLang] = useState("");
    const [folder, setFolder] = useState(1);
    const subtitleContextKey = JSON.stringify([mediaId, folder, lang, originalLang, urlLang]);
    const { state: subtitlesRequest, run: runSubtitleRequest, isCurrentContext: isSubtitleContextCurrent } = useSubtitleRequest(subtitleContextKey);
    const mediaFolderRef = useRef({ mediaId, folder });
    mediaFolderRef.current = { mediaId, folder };
    const { duration: audioDuration, setDuration: setAudioDuration } = useOriginalAudioDuration(mp3, JSON.stringify([mediaId, folder]));
    const durationFull = useMemo(() => timelineDuration(audioDuration, folder,
        subtitlesRequest.hasData ? translationTrackAll[originalLang] : [],
        subtitlesRequest.hasData ? translationTrackAll[lang] : [],
    ), [audioDuration, folder, subtitlesRequest.hasData, translationTrackAll, originalLang, lang]);
    const [foldersTotal, setFoldersTotal] = useState(1);
    const [playbackRate, setPlaybackRateState] = useState(() => {
        const saved = localStorage.getItem("playbackRate");
        return saved ? Number(saved) : 1;
    });

    const setPlaybackRate: React.Dispatch<React.SetStateAction<number>> = useCallback((val: React.SetStateAction<number>) => {
        setPlaybackRateState((prev) => {
            const next = typeof val === "function" ? val(prev) : val;
            localStorage.setItem("playbackRate", String(next));
            return next;
        });
    }, []);
    const [subtitleEditing, setSubtitleEditing] = useState(false);
    const [translatedSubtitleId, setTranslatedSubtitleId] = useState<null | string>(null);
    const [editedTrackId, setEditedTrackId] = useState<null | string>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [captionsSize, setCaptionsSize] = useState<ICaptionSizeState>({ [originalLang]: 12 });

    const [showOriginalLanguage, setShowOriginalLanguage] = useState(false);
    const [playingFragment, setPlayingFragment] = useState<string | null>(null);
    const [playingFragmentRestart, setPlayingFragmentRestart] = useState(0);
    const [playingFragmentNext, setPlayingFragmentNext] = useState<AudioTrack | null>(null);
    const [changedAudioTrackIds, setChangedAudioTrackIds] = useState<string[] | null>(null);
    const [playSavedTrack, setPlaySavedTrack] = useState(false);
    // () => {
    // const saved = localStorage.getItem('showOriginalLanguage');
    // return saved !== null ? JSON.parse(saved) : false; // Default to false if not found
    // }

    const [volumeOriginal, setVolumeOriginalState] = useState(() => {
        const saved = localStorage.getItem("volumeOriginal");
        return saved ? Number(saved) : 40;
    });
    const [volumeTranslated, setVolumeTranslatedState] = useState(() => {
        const saved = localStorage.getItem("volumeTranslated");
        return saved ? Number(saved) : 60;
    });

    const setVolumeOriginal: React.Dispatch<React.SetStateAction<number>> = useCallback((val: React.SetStateAction<number>) => {
        setVolumeOriginalState((prev) => {
            const next = typeof val === "function" ? val(prev) : val;
            localStorage.setItem("volumeOriginal", String(next));
            return next;
        });
    }, []);

    const setVolumeTranslated: React.Dispatch<React.SetStateAction<number>> = useCallback((val: React.SetStateAction<number>) => {
        setVolumeTranslatedState((prev) => {
            const next = typeof val === "function" ? val(prev) : val;
            localStorage.setItem("volumeTranslated", String(next));
            return next;
        });
    }, []);

    const [zoomLevel, setZoomLevelState] = useState<number>(() => {
        const savedZoomLevel = localStorage.getItem("zoomLevel");
        return savedZoomLevel ? JSON.parse(savedZoomLevel) : 80; // Default to 80 if not found
    });

    const [newData, setNewData] = useState<AudioTrack[]>([]);

    const [changedRecently, setChangedRecently] = useState<string[]>([]);
    const [changed, setChanged] = useState<string[]>([]);
    const [changedTranslation, setChangedTranslation] = useState<string[]>([]);
    const [changedRus, setChangedRus] = useState<string[]>([]);
    const [changedReels, setChangedReels] = useState<string[]>([]);
    const [changeToSave, setChangeToSave] = useState<Change | string | null>(null);
    const [editingSubtitles, setEditingSubtitles] = useState<string | null>(null);
    const [scrollTo, setScrollTo] = useState<number | null>(null);

    const [video, setVideo] = useState<string>();
    const [russian, setRussian] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");

    const [savingStatus, setSavingStatus] = useState(false);
    const [trackSelected, setTrackSelected] = useState<string | null>(null);
    // Translated-led split/merge: its own selection state so a half-done RU merge
    // selection can never cross with a translated-side merge, and its own error
    // surface (banner, not alert — backend 409/422 texts are shown verbatim).
    const [translatedTrackSelected, setTranslatedTrackSelected] = useState<string | null>(null);
    const [translatedOpError, setTranslatedOpError] = useState<string | null>(null);
    // «Наложение снято…» — что машина сдвинула сама (режим «Без автоправок», правило владельца
    // 01.10.2026). Не отказ: своя нейтральная полоса, а не красный канал translatedOpError, который
    // окно разбиения показывает как ошибку своей операции.
    const [overlapPushNotice, setOverlapPushNotice] = useState<string | null>(null);
    // Реплика, которую надо выделить и отдать ей фокус, как только она появится на дорожке
    // (новая реплика «+»). Исполняет SubtitlesBlock: у него лента и поле ввода.
    const [focusCue, setFocusCue] = useState<FocusCueRequest | null>(null);
    const clearFocusCue = useCallback(() => setFocusCue(null), []);
    const [editedRecommendationSubtitle, setEditedRecommendationSubtitle] = useState<string | null>(
        null,
    );

    //TODO: при изменении генерировать массив и передавать на сервер
    const [voicingSpaces, setVoicingSpaces] = useState<ISpace[]>([]);
    const [voicingSpacesAllPages, setVoicingSpacesAllPages] = useState<Record<string, ISpace[]>>(
        {},
    );
    const [voicingSpacesDeleted, setVoicingSpacesDeleted] = useState<string[]>([]);
    //TODO: при включении основной плеер при проигрывании должен перепрыгивать промежутки
    const [voicingSpacesOn, setVoicingSpacesOn] = useState<boolean>(false);
    const [voicingSpacesMinimum, setVoicingSpacesMinimum] = useState<number>(
        // single-knob model: "Minimum interval" 0; the indent alone defines the cut
        parseInt(localStorage.getItem("voicingSpacesMinimum") ?? "0"),
    );
    const [voicingSpacesMinimumOffset, setVoicingSpacesMinimumOffset] = useState<number>(
        // "Silence Cut Indents" 3s each side -> cut pauses > 6s, trimming them down to 6s
        parseInt(localStorage.getItem("voicingSpacesMinimumOffset") ?? "3000"),
    );
    const [disableMovementRestrictionsState, setDisableMovementRestrictionsState] = useState(
        () => localStorage.getItem("disableMovementRestrictions") === "1",
    );
    const [catVideoFolderUrl, setCatVideoFolderUrl] = useState<Record<string, string>>({});

    const [videoPlayerType, setVideoPlayerType2] = useState(getPlayerFromLocal());
    const [lastFragmentTrimmed, setLastFragmentTrimmed] = useState(false);
    const [subtitleMode, setSubtitleMode] = useState(false);
    const [selectedTranslatedTrackIds, setSelectedTranslatedTrackIds] = useState<SelectedTranslatedTrack[]>([]);
    const [selectedOriginalTrackIds, setSelectedOriginalTrackIds] = useState<SelectedTranslatedTrack[]>([]);

    // Local-first editing state
    const [dirtyTracks, setDirtyTracks] = useState<Map<string, DirtyTrackChange>>(new Map());
    const dirtyTracksRef = useRef<Map<string, DirtyTrackChange>>(dirtyTracks);
    const flushPromiseRef = useRef<Promise<void> | null>(null);
    // Счётчик локальных перемен: правка помечена несохранённой, сохранение началось или кончилось.
    // Дозагрузка эфира берёт реплики с сервера, только если за время её запроса он не сдвинулся:
    // ответ, ушедший с сервера до записи, вернул бы на экран прежнее (см. liveRefreshMerge).
    const localEditEpochRef = useRef(0);
    const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Последнее «Не сохранено…», показанное в полосе ошибок: удачное сохранение снимает именно его.
    const saveErrorShownRef = useRef<string | null>(null);
    // Правки, которые сервер не принял в последний раз (ключи грязной карты), и их число на экране:
    // по нему полоса ошибок предлагает «Сбросить несохранённое».
    const refusedKeysRef = useRef<Set<string>>(new Set());
    const [refusedEdits, setRefusedEdits] = useState(0);
    // Данные папок для слов об ошибке («реплика 0:04») внутри колбэков, без лишних зависимостей.
    const subtitlesDataAllRef = useRef(subtitlesDataAll);
    // Доделать куски разбиения (перевод, если родительский не разрезался, и озвучка). Объявлено
    // здесь, а заполняется ниже, после озвучки: разбиение стоит в файле раньше неё.
    const completeSplitPiecesRef = useRef<((ids: string[], targetLang: ILang) => Promise<void>) | null>(null);
    subtitlesDataAllRef.current = subtitlesDataAll;
    // Строки на экране (у оригинала — с пометками кусков фраз): по ним удаление и склейка куска
    // спрашивают, называя кусок и его перевод, без лишних зависимостей колбэков.
    const translationTrackAllRef = useRef(translationTrackAll);
    translationTrackAllRef.current = translationTrackAll;
    const prevFolderRef = useRef(folder);
    const realtimeFolderSignatureRef = useRef<string | null>(null);
    // Mirror projectData for use inside the autosave callback without dep churn.
    const projectDataRef = useRef<any>(null);
    // Russian source cue ids edited (caption changes) per folder, for the manual
    // "Apply Russian to all languages" button.
    const editedRuIdsByFolderRef = useRef<Record<number, Set<string>>>({});
    const lastActiveSubtitleRef = useRef<{ playOriginalStart: number; playbackTime: number } | null>(null);
    const lastActiveVoiceoverRef = useRef<{ trackId: string; playOriginalStart: number; playbackTime: number } | null>(null);

    useEffect(() => {
        // Save to local storage whenever showOriginalLanguage changes
        localStorage.setItem("showOriginalLanguage", JSON.stringify(showOriginalLanguage));
    }, [showOriginalLanguage]);

    // Режим "озвучка+видео без оригинала" живёт только до остановки воспроизведения —
    // как восстановление громкостей в useLastActiveShortcuts
    useEffect(() => {
        if (!playing) {
            setVoiceoverOnlyPlayback(false);
        }
    }, [playing]);

    useEffect(() => {
        localStorage.setItem(
            LocalStorageKeys.useCheckedVoiceoverSynthesis,
            useCheckedVoiceoverSynthesis ? "1" : "0",
        );
    }, [useCheckedVoiceoverSynthesis]);

    useEffect(() => {
        if (changedAudioTrackIds) {
            localStorage.setItem(
                LocalStorageKeys.changedAudio + folder,
                JSON.stringify(changedAudioTrackIds),
            );
        }
    }, [changedAudioTrackIds]);

    useEffect(() => {
        const localData = localStorage.getItem(LocalStorageKeys.changedAudio + folder);
        if (localData) {
            try {
                const data = JSON.parse(localData);
                setChangedAudioTrackIds(data);
            } catch (err) {
                setChangedAudioTrackIds([]);
                console.log("local storage parse error changedAudio", err);
            }
        }
    }, []);

    useEffect(() => {
        if (
            playSavedTrack &&
            !playingFragment &&
            changedAudioTrackIds &&
            changedAudioTrackIds.length
        ) {
            setPlayingFragment(changedAudioTrackIds[0]);
        }
    }, [playSavedTrack, playingFragment]);

    useEffect(() => {
        if (changedAudioTrackIds && changedAudioTrackIds.length < 1) {
            setPlaySavedTrack(false);
        }
        if (!playingFragment) {
            return;
        }
        if (changedAudioTrackIds && changedAudioTrackIds.includes(playingFragment)) {
            setChangedAudioTrackIds((prev) =>
                (prev || []).filter((item) => item !== playingFragment),
            );
        }
    }, [changedAudioTrackIds, playingFragment]);

    const setZoomLevel = (newZoomLevel: number) => {
        setZoomLevelState(newZoomLevel);
        localStorage.setItem("zoomLevel", JSON.stringify(newZoomLevel));
    };

    const setVideoPlayerType = (typ: string) => {
        localStorage.setItem("playerType", typ);
        setVideoPlayerType2(typ);
    };

    const setDisableMovementRestrictions: React.Dispatch<React.SetStateAction<boolean>> =
        useCallback((val: React.SetStateAction<boolean>) => {
            setDisableMovementRestrictionsState((prev) => {
                const next = typeof val === "function" ? val(prev) : val;
                localStorage.setItem("disableMovementRestrictions", next ? "1" : "0");
                return next;
            });
        }, []);

    useEffect(() => {
        if (lang !== originalLang && subtitlesDataAll[lang]) {
            setVoicingSpacesAllPages(
                getSpacesForAllPages({
                    // Место озвучки без реплики оригинала — пауза озвучки: в видео её нет.
                    tracksAll: markOrphanVoicingsInFolders(subtitlesDataAll[lang], subtitlesDataAll[originalLang]),
                    voicingSpacesMinimumOffset,
                    voicingSpacesMinimum,
                }),
            );
        }
    }, [translationTrackAll[lang], voicingSpacesMinimumOffset, voicingSpacesMinimum, originalLang]);

    // Хук режима «Без автоправок» создаётся ниже (ему нужен flushGate), а загрузки папок описаны
    // раньше — они достают его через ссылку. Загрузка одного оригинала (без пары) флага не несёт.
    const manualOnlyDataReadRef = useRef<ManualOnlyControl["beginDataRead"] | null>(null);
    const beginManualOnlyDataRead = useCallback(
        (
            mId: string,
            pairLang: ILang,
            languageFrom?: ILang,
            source: "data" | "merge" = "data",
        ): ((flag: boolean | undefined) => void) => {
            const begin = manualOnlyDataReadRef.current;
            if (!begin || !languageFrom) return () => undefined;
            return begin({ mediaId: mId, lang: wireLang(pairLang), languageFrom: wireLang(languageFrom) }, source);
        },
        [],
    );

    // «Без автоправок»: мерка дублей (`dub_ms`, контракт C1) приходит в данных папок, только если
    // пара была ручной, когда сервер их читал. Включился режим не загрузкой данных (переключатель
    // здесь, перечитывание флага по фокусу или по времени, дозагрузка эфира) — в данных её нет, и
    // раздвижка мерила бы дубль json `length`, а у кусков разбиения и реплик «+» это слот: она
    // толкала бы соседку, до которой дубль не дотягивает, и сохраняла это как машинный сдвиг
    // (`manual_overlap_push`, ревью 01.10.2026). Пока такая папка не перечитана, поднят
    // `dubMeasurePendingRef`, и раздвижка редактора в ручной паре никого не толкает
    // (cascadeNeighbours). Опускает его загрузка данных пары целиком, пришедшая от сервера,
    // который знал о режиме (флаг в папках не false), и выключение режима.
    const dubMeasurePendingRef = useRef(false);
    // Загрузка данных пары, которая ещё в пути: включившийся режим ждёт её, а не шлёт вторую.
    const pairReadInFlightRef = useRef<Promise<boolean> | null>(null);

    const getAndSaveSubtitles = useCallback(
        async (mediaId: string, lang: ILang, languageFrom?: ILang, callback?: () => void) => {
            // Каждая загрузка папок пары несёт и флаг «Без автоправок» — пусть он ляжет, если
            // свежее его ничего нет: флаг могли переключить и не в этой вкладке.
            const flagLanded = beginManualOnlyDataRead(mediaId, lang, languageFrom);
            // true — данные этого запроса легли на экран (см. useSubtitleRequest).
            const request = runSubtitleRequest(() => getAllSubtitlesAction({ mediaId, lang, languageFrom }), (data) => {
                if (Array.isArray(data)) {
                    const flag = manualOnlyFlagOfFolders(data);
                    flagLanded(flag);
                    // Данные пары легли целиком: сервер, знавший о режиме, положил в них свою мерку
                    // дублей; сервер, не присылающий флага (undefined), не мерит вовсе — ждать
                    // нечего. false — сервер читал папку, когда пара ещё не была ручной.
                    if (languageFrom && flag !== false) dubMeasurePendingRef.current = false;
                    debug && console.log("data = ", JSON.stringify(data));
                    // Данные целевого языка и (если есть original_translations) исходного ложатся
                    // ОДНИМ обновлением. React 17 не склеивает два setState в асинхронном колбэке:
                    // между ними колонка озвучки видела новые реплики перевода при старом оригинале
                    // и называла только что появившуюся реплику «озвучкой без реплики оригинала»
                    // (см. orphanVoicings.ts).
                    const originalData = languageFrom
                        ? data.map((folder: any) => ({
                              ...folder,
                              translations: folder.original_translations || [],
                          }))
                        : null;
                    setSubtitlesDataAll((prev) =>
                        languageFrom && originalData
                            ? { ...prev, [lang]: data, [languageFrom]: originalData }
                            : { ...prev, [lang]: data },
                    );

                    callback?.();
                } else throw new Error("Invalid subtitle response");
            });
            if (languageFrom) {
                pairReadInFlightRef.current = request;
                const settled = () => {
                    if (pairReadInFlightRef.current === request) pairReadInFlightRef.current = null;
                };
                request.then(settled, settled);
            }
            return request;
        },
        [runSubtitleRequest, beginManualOnlyDataRead],
    );

    const getAllSubtitles = useCallback(
        async (gettedLang?: ILang) => {
            let locale = gettedLang || lang;
            // Один запрос с language_from вместо двух отдельных
            let languageFrom = locale !== originalLang ? originalLang : undefined;
            if (locale === originalLang && urlLang && urlLang !== originalLang) {
                languageFrom = originalLang;
                locale = urlLang as ILang; // Force fetch of the pair (e.g. ru-de)
            }
            console.log("getAllSubtitles calling with:", { locale, languageFrom, urlLang, lang });
            return getAndSaveSubtitles(mediaId, locale, languageFrom);
        },
        [getAndSaveSubtitles, mediaId, lang, originalLang, urlLang],
    );

    useEffect(() => {
        if (mediaId && fastApiReady && fastApiMediaId === mediaId) void getAllSubtitles();
    }, [mediaId, fastApiReady, fastApiMediaId, getAllSubtitles]);

    // --- Live (stream) incremental refresh -------------------------------------------------
    // Grow the audio track in place (no mp3 clear -> no waveform unmount/flicker).
    const refreshRusAudioLive = useCallback(async (mId: string, f: number) => {
        const { data } = await getDownloadedRusAudioUrl(`${mId}/files_to_send/${f}.mp3`);
        if (!data || mediaFolderRef.current.mediaId !== mId || mediaFolderRef.current.folder !== f) return;
        // NOT WHILE IT IS PLAYING.
        //
        // Clearing mp3 was already avoided here, to stop the waveform
        // unmounting -- but handing over a NEW blob URL for the same track has
        // the same effect on playback: the source identity changes, the media
        // element reloads, and the playhead lands back at zero. On a live folder
        // this runs on every refresh, so listening to one was impossible; it
        // restarted every few seconds.
        //
        // The grown audio is still wanted. It waits until playback stops, which
        // is the only moment swapping the source costs nothing.
        if (playingRef.current) {
            pendingMp3Ref.current = data;
            return;
        }
        pendingMp3Ref.current = null;
        setMp3(data);
    }, []);

    // Pull newly-transcribed Russian cues + the grown audio, additively (never clobber edits).
    const liveRefresh = useCallback(async () => {
        if (!mediaId || projectDataRef.current?.source !== "realtime_stream") return;
        try {
            void refreshRusAudioLive(mediaId, folder);
            let locale: ILang = lang;
            let languageFrom: ILang | undefined = locale !== originalLang ? originalLang : undefined;
            if (locale === originalLang && urlLang && urlLang !== originalLang) {
                languageFrom = originalLang;
                locale = urlLang as ILang;
            }
            // Дозагрузка дописывает только новые реплики, прежние остаются со старыми полями: флаг,
            // включившийся отсюда, мерки дублей прежним не приносит (`merge`, см. dubMeasurePendingRef).
            const flagLanded = beginManualOnlyDataRead(mediaId, locale, languageFrom, "merge");
            const epochAtRequest = localEditEpochRef.current;
            const savingAtRequest = Boolean(flushPromiseRef.current);
            const response = await getAllSubtitlesAction({ mediaId, lang: locale, languageFrom });
            if (!isSubtitleContextCurrent()) return;
            if (response?.data && Array.isArray(response.data)) {
                flagLanded(manualOnlyFlagOfFolders(response.data));
                // Перевод и оригинал — одним обновлением (см. getAndSaveSubtitles).
                const originalData =
                    languageFrom && response.data.length > 0
                        ? response.data.map((fo: any) => ({
                              ...fo,
                              translations: fo.original_translations || [],
                          }))
                        : null;
                const lf = languageFrom as ILang;
                // Реплики без несохранённой правки — как на сервере, пропавшие с сервера уходят
                // (liveRefreshMerge: иначе в редакторе жили родитель разрезанной фразы и длины до
                // озвучки, и раздвижка ♪ ставила по ним куски — P50m10d). Пока шёл запрос, что-то
                // правили или сохраняли — ответ мог уйти с сервера раньше записи: только дописать новые.
                const quiet =
                    !savingAtRequest && !flushPromiseRef.current && epochAtRequest === localEditEpochRef.current;
                const merge = (prevFolders: any, freshFolders: any[], rowLang: ILang) =>
                    quiet
                        ? mergeSubtitleFoldersLive(prevFolders, freshFolders, (id) =>
                              hasUnsavedChange(dirtyTracksRef.current.keys(), rowLang, id),
                          )
                        : mergeSubtitleFoldersAdditive(prevFolders, freshFolders);
                setSubtitlesDataAll((prev) => ({
                    ...prev,
                    [locale]: merge((prev as any)[locale], response.data, locale),
                    ...(originalData ? { [lf]: merge((prev as any)[lf], originalData, lf) } : {}),
                }));
            }
        } catch (e) {
            console.warn("[liveRefresh] failed", e);
        }
    }, [mediaId, folder, lang, originalLang, urlLang, refreshRusAudioLive, isSubtitleContextCurrent, beginManualOnlyDataRead]);

    // Poll while this is a live stream so the editor fills in cue-by-cue and the audio grows.
    useEffect(() => {
        if (!mediaId) return;
        const id = setInterval(() => {
            if (projectDataRef.current?.source === "realtime_stream") void liveRefresh();
        }, 20000);
        return () => clearInterval(id);
    }, [mediaId, liveRefresh]);

    useEffect(() => {
        if (offline || !mediaId) return;
        let cancelled = false;
        setFastApiReady(false);
        setFastApiMediaId("");
        setIsStreamEditor(false);
        setRealtimeEditorStatus(null);
        realtimeFolderSignatureRef.current = null;
        setAudioDuration(0);
        setMp3(undefined);
        pendingMp3Ref.current = null;
        setPlaybackTime(0);
        setPlaying(false);
        setPlayingFragment(null);
        setChanged(getFromLocal(mediaId, ChangedDataKeys.changed, folder));
        setChangedTranslation(getFromLocal(mediaId, ChangedDataKeys.changedTranslation, folder));
        setChangedRus(getFromLocal(mediaId, ChangedDataKeys.changedRus, folder));
        setCaptionsSize(getCaptionSizeFromLocal(lang));
        // Resolve the stream-only API before issuing any editor request. A lecture
        // keeps the previous base URL, therefore its behaviour stays unchanged.
        void (async () => {
            const isRealtimeStream = await resolveFastApiForMedia(mediaId);
            if (cancelled) return;
            setIsStreamEditor(isRealtimeStream);
            setFastApiMediaId(mediaId);
            setFastApiReady(true);
            await getAndSaveFolders(mediaId);
            if (cancelled) return;
            // Fetch lecture name from project_data.json
            getProjectDataApi(mediaId)
            .then((data: any) => {
                setLectureName(data?.name || '');
                setProjectData(data);
                projectDataRef.current = data;
            })
            .catch(() => {
                setLectureName('');
                setProjectData(null);
                projectDataRef.current = null;
            });
        })();
        return () => { cancelled = true; };
    }, [mediaId]);

    useEffect(() => {
        setLectureStatus(computeLectureStatus(projectData, lang));
    }, [projectData, lang]);

    const changeCaptionSize = ({ lang, size }: { lang: ILang; size: number }) => {
        setCaptionsSize((prev) => {
            const newCaptionSize = { ...prev, [lang]: size };
            localStorage.setItem(LocalStorageKeys.captionSize, JSON.stringify(newCaptionSize));
            return newCaptionSize;
        });
    };

    const getElementById = useCallback(
        (id: string, lang: ILang = originalLang) => {
            if (!translationTrackAll[lang]?.length) {
                return null;
            }
            return translationTrackAll[lang].find((item) => item.id === id) || null;
        },
        [translationTrackAll, originalLang],
    );

    useEffect(() => {
        if (time) {
            setScrollTo((time || 0) / (100 - zoomLevel));
        }
    }, [time, zoomLevel]);

    const scrollToElement = useCallback(
        (id: string, lang: ILang = originalLang) => {
            if (!id) setScrollTo(null);
            if (translationTrackAll[lang]?.length) {
                setScrollTo((getElementById(id)?.start || 0) / (100 - zoomLevel) - 200);
                setTimeout(() => {
                    setChangedRecently([id]);
                }, 700);
            }
        },
        [translationTrackAll, zoomLevel, getElementById, originalLang],
    );

    const showSubtitleEditor = useCallback((id?: string) => {
        if (!id) {
            setChangedRecently([]);
            setEditingSubtitles(null);
            setTimeout(() => {
                setChangedRecently([]);
            }, 2000);
            return;
        }
        setEditingSubtitles(id);
        // scrollToElement(id)
        setChangedRecently([id]);
        setTimeout(() => {
            setChangedRecently([]);
        }, 2000);
    }, []);

    const saveToLocal = useCallback(() => {
        const key = folder ? `${mediaId}_${folder}` : mediaId;
        localStorage.setItem(
            key,
            JSON.stringify({
                play: playingFragment,
                changed: changed,
                changedTranslation: changedTranslation,
                changedRus: changedRus,
                changedReels: changedReels,
            }),
        );
    }, [mediaId, playingFragment, changed, folder]);

    const prepareData = useCallback(
        (allData: AudioFolderData[], folder_num: number, manualOnly = false, sourceIds: string[] | null = null) => {
            const folder = folder_num - 1;
            let translationsArray = allData[folder]?.["translations"] || [];
            if (!translationsArray.length) {
                return [];
            }
            // Озвучка без реплики оригинала в этой папке (4119e839, ru_es, папка 1, 1:22, «Así es.»,
            // 02.10.2026: русскую реплику удалили, испанский перевод и wav остались) помечается ДО
            // раскладки: она молчит (voicingSilenced) — не толкает соседей ни здесь, ни в
            // расписании и границах перетаскивания, которые читают эту колонку.
            if (sourceIds) {
                const marked = markOrphanVoicings(translationsArray, sourceIds);
                if (marked.some((track, i) => track !== translationsArray[i])) {
                    translationsArray = marked;
                    allData = allData.map((folderData, i) =>
                        i === folder ? { ...folderData, translations: marked } : folderData,
                    );
                }
            }
            translationsArray = translationsArray
                .map((one) => prepareDataOne({ one, folder_num, allData }))
                .sort((a, b) => a.start - b.start);
            if (manualOnly) {
                // «Без автоправок»: лента показывает файлы как есть. Раскладка ниже рисовала бы
                // наехавшую озвучку отодвинутой по своим правилам (зазор 50 мс), предпрослушивание
                // играло бы нарисованное, а перетаскивание сохраняло бы «нарисованное + сдвиг», то
                // есть машинный отступ как работу переводчика. Загрузка ничего не двигает и не
                // сохраняет: наезд остаётся на экране и помечен, а снимает его следующая правка в
                // папке — сдвигом позже (cascadeNeighbours, правило владельца 01.10.2026).
                return markManualOverlaps(translationsArray as unknown as AudioTrack[]);
            }
            // Fix overlapping translated tracks on load (TTS can be longer than originals).
            // adjustAllFragments returns EVERY cue, including the ones with no wav yet —
            // this assignment replaces the whole column, and a cue missing from it is a cue
            // the translator can no longer see is missing its voiceover.
            if (translationsArray.some((t: any) => !!t.file)) {
                const { shifted } = adjustAllFragments(translationsArray as unknown as AudioTrackData[]);
                if (shifted.length > 0) {
                    translationsArray = shifted as unknown as AudioTrack[];
                }
            }
            // setLang(translationsArray[0].lang)

            return translationsArray;
        },
        [],
    );

    const updateRusData = useCallback(() => {
        const folderIndex = folder - 1;
        if (!subtitlesDataAll[lang]?.length) {
            return [];
        }
        const rusTranslationsArray =
            subtitlesDataAll[originalLang]?.[folderIndex]?.["translations"] || [];
        const langTranslationsArray = subtitlesDataAll[lang][folderIndex]?.["translations"] || [];

        if (!rusTranslationsArray.length) {
            return [];
        }
        const transCaptions = langTranslationsArray.reduce(
            (acc, translation) => {
                acc[translation.id] = {
                    captions: translation.captions,
                    voice: translation.voice || "male",
                    speed: translation.speed || 1,
                    translationStart: translation.start,
                };
                return acc;
            },
            {} as Record<
                string,
                Pick<AudioTrackData, "voice" | "captions" | "speed" | "translationStart">
            >,
        );
        // Есть ли у строки пара перевода на сервере и цело ли её время: по этому ♪ заранее
        // говорит, что озвучить нельзя и почему, — вместо отказа сервера после нажатия.
        const targetById = new Map(langTranslationsArray.map((t) => [t.id, t]));
        const trackTranslationsArray: AudioTrack[] = rusTranslationsArray
            .map((one) => {
                const target = targetById.get(one.id);
                return {
                    ...prepareDataOne({
                        one,
                        folder_num: folder,
                        allData: subtitlesDataAll[originalLang] || [],
                        transCaption: transCaptions[one.id],
                    }),
                    has_translation: Boolean(target),
                    ...(target ? pairRowFields(target) : { translation_timing_invalid: false }),
                    // Кусок фразы: родитель записан в json оригинала, а у старых кусков — хотя бы в
                    // json перевода.
                    split_parent: one.split_parent ?? target?.split_parent,
                };
            })
            .sort((a, b) => a.start - b.start);
        // translationsArray = translationsArray.filter((one) => one["folder_num"] === folder);
        // Куски одной фразы несут номер: колонка оригинала не повторяет общую русскую фразу, а
        // удаление и склейка куска спрашивают, что это не дубль (01.10.2026, 4119e839).
        return markPhrasePieces(trackTranslationsArray);
    }, [folder, subtitlesDataAll, lang, originalLang]);

    const saveSubtitleChangeOne = useCallback(
        async (arg: EditSubtitleArgs) => {
            saveToLocal();

            // Determine if we are editing the source language (based on original intent of the caller)
            const isEditingSource = arg.lang === wireLang(originalLang);

            // Fix for isolated editing: override lang if needed
            if (isEditingSource && urlLang && urlLang !== originalLang) {
                arg.lang = wireLang(urlLang);
            }
            console.log("saveSubtitleChangeOne CALLED", { arg, internalState: { lang, originalLang, urlLang } });

            setSavingStatus(true);
            try {
                setSubtitleEditing(true);
                let data;
                // If original intent was source editing, we use editRusSubtitleAction (which maps to metadata_edit_subtitle)
                // even if we changed the target lang due to isolation.
                if (isEditingSource) {
                    data = await editRusSubtitleAction(arg);
                } else {
                    data = await editTranslatedSubtitleAction(arg);
                }
            } catch (error) {
                console.error("Error in saveMoving:", error);
                throw error;
            } finally {
                setSubtitleEditing(false);
                setSavingStatus(false);
            }
            // To be safe, let's keep it simple:
            // We want to pass the correct lang to the ACTION.
            // But we need to choose the action based on WHAT we are editing.
        },
        [saveToLocal, originalLang, urlLang],
    );

    // --- Local-first auto-save infrastructure ---

    const flushDirtyChanges = useCallback(async () => {
        const current = dirtyTracksRef.current;
        if (current.size === 0) {
            return;
        }
        const snapshot = new Map(current);
        setDirtyTracks(new Map());
        dirtyTracksRef.current = new Map();
        setAutoSaveStatus('saving');

        // Реплики, которые сервер не записал, — по ключу грязной карты, с причиной его словами.
        const refused = new Map<string, string>();
        const keyOf = dirtyKeyOf;
        // Машинные сдвиги, не отправленные потому, что строку переводчика той же реплики сервер
        // не принял: ушли бы сейчас — повтор его строки потом вернул бы реплику на чужой дубль.
        // Это не отказ сервера: они ждут в несохранённых и уходят следом за его строкой.
        const held: DirtyTrackChange[] = [];
        // Машинный сдвиг, который сервер не принял по самой реплике (сдвиг не позже её места,
        // наложения на диске уже нет и т. п. — сервер судит сдвиг по своему диску). Это не правка
        // человека: «Не сохранено» и запрет ♪ до сброса здесь неверны. Сдвиг снимается, папка
        // перечитывается — на экране то, что на диске, а красная метка покажет наезд, если он остался.
        const droppedPushes: { change: DirtyTrackChange; reason: string }[] = [];

        const doFlush = async () => {
            const allChanges = Array.from(snapshot.values());
            // Не ответил весь запрос или пакет сорвался посреди — это не отказ по реплике.
            let transient = false;
            try {
                const translatedLangChanges = allChanges
                    .filter(c => c.lang !== originalLang);
                const sourceLangChanges = allChanges
                    .filter(c => c.lang === originalLang);

                // Сдвиги, сделанные машиной (наложение, снятое в режиме «Без автоправок», правило
                // владельца 01.10.2026), уходят своим запросом с пометкой в заголовке — журнал
                // сервера пишет их как машинные, а не как правку переводчика. В нём только они:
                // запись сдвига — своя у реплики, со строкой переводчика она не сливается (см.
                // markDirty). Правки человека идут первыми, сдвиги — следом: если реплику тронули
                // оба, на диске остаётся сдвиг. Без режима таких записей нет: один запрос.
                const translatedGroups = [
                    translatedLangChanges.filter((c) => !c.origin),
                    translatedLangChanges.filter((c) => c.origin === "manual_overlap_push"),
                ];
                for (const candidates of translatedGroups) {
                    const origin = candidates[0]?.origin;
                    const group = origin
                        ? candidates.filter((c) => {
                              if (!refused.has(keyOf({ ...c, origin: undefined }))) return true;
                              held.push(c);
                              return false;
                          })
                        : candidates;
                    if (group.length === 0) continue;
                    const body = group.map(c => {
                        // Правка времени живого эфира: только место и/или длина, которые человек
                        // сдвинул сам (liveTiming.ts), — слот реплики правка дубля не трогает.
                        const timing = c.liveTiming ? liveTimingWire(c.fields) : null;
                        return {
                        id: c.trackId,
                        folder_num: c.folderNum,
                        ...(c.liveTiming
                            ? (timing ? { stream_timing: timing } : {})
                            : {
                                  start_rus_milli: c.fields.start_rus_milli ?? c.fields.start,
                                  end_rus_milli: c.fields.end_rus_milli ?? (
                                      (c.fields.start !== undefined && c.fields.length !== undefined)
                                          ? c.fields.start + c.fields.length
                                          : undefined
                                  ),
                              }),
                        lang: wireLang(lang),
                        update_recommendation: false,
                        language_from: wireLang(originalLang),
                        ...(c.fields.captions !== undefined ? { captions: c.fields.captions } : {}),
                        ...(c.fields.voice !== undefined ? { voice: c.fields.voice } : {}),
                        ...(c.fields.speed !== undefined ? { speed: c.fields.speed } : {}),
                        };
                    });
                    const result = await editMassTranslatedSubtitleAction({
                        mediaId,
                        body: body as any,
                        ...(origin ? { origin } : {}),
                    });
                    if (!result.data) {
                        // Не ответил весь пакет (сеть, 5xx): не записана ни одна реплика.
                        transient = true;
                        group.forEach((c) =>
                            refused.set(keyOf(c), result.error || "сервер не ответил"),
                        );
                    } else {
                        // Ответил — но мог отказать по отдельным репликам: старый сервер кладёт
                        // отказ в results[i].error при статусе 200, новый добавляет failed (207).
                        // Раньше такой ответ считался успехом, и текст переводчика пропадал.
                        const failed = massEditFailures(
                            group.map((c) => c.trackId),
                            result.data,
                        );
                        group.forEach((c) => {
                            const reason = failed.get(c.trackId);
                            if (reason === undefined) return;
                            if (origin === "manual_overlap_push") {
                                droppedPushes.push({ change: c, reason });
                                return;
                            }
                            refused.set(keyOf(c), reason);
                        });
                        // Сервер мог создать файл перевода из первого текста (у реплики «+» его не
                        // было) — пусть пара появится и на экране, иначе ♪ до перезагрузки считал
                        // бы, что перевода нет.
                        const written = (Array.isArray(result.data.results) ? result.data.results : [])
                            .map((item: any) => item?.update_subtitle)
                            .filter((one: any) => one && typeof one === "object" && one.id);
                        group.forEach((c) => {
                            const mine = written.filter((one: any) => String(one.id) === c.trackId);
                            if (!mine.length || refused.has(keyOf(c))) return;
                            setSubtitlesDataAll((prev) =>
                                addMissingTracksToSubtitlesData(prev, c.lang, c.folderNum, mine as AudioTrackData[]),
                            );
                        });
                    }
                }

                for (const c of sourceLangChanges) {
                    const targetLang = urlLang && urlLang !== originalLang
                        ? wireLang(urlLang)
                        : wireLang(lang);
                    const saved: any = await editRusSubtitleAction({
                        id: c.trackId,
                        mediaId,
                        folder_num: c.folderNum,
                        lang: targetLang as MetadataToSubtitlesLanguages,
                        language_from: wireLang(originalLang),
                        update_recommendation: false,
                        ...(c.fields.start_rus_milli !== undefined ? { start_rus_milli: c.fields.start_rus_milli } : {}),
                        ...(c.fields.end_rus_milli !== undefined ? { end_rus_milli: c.fields.end_rus_milli } : {}),
                        ...(c.fields.captions !== undefined ? { captions: c.fields.captions } : {}),
                    });
                    if (saved && typeof saved.error === "string") {
                        refused.set(keyOf(c), saved.error);
                    }
                }

                // Record edited Russian source cues (caption changes only) per folder for the
                // manual "Apply Russian to all languages" button. Nothing auto-fires from the
                // autosave path anymore: recreation is explicit now
                // (/recreate_translated_from_ru or the button), owner's decision.
                for (const c of sourceLangChanges) {
                    if (c.fields.captions === undefined) continue; // skip pure-timing edits
                    if (refused.has(keyOf(c))) continue; // не записана — запомнится, когда ляжет
                    // Stream endpoints enqueue their own structural diff after every
                    // autosave. The legacy accumulator remains lecture-only.
                    if (projectDataRef.current?.source === "realtime_stream") continue;
                    const acc = editedRuIdsByFolderRef.current[c.folderNum]
                        || (editedRuIdsByFolderRef.current[c.folderNum] = new Set<string>());
                    acc.add(c.trackId);
                }
            } catch (e) {
                // Неожиданный сбой посреди пакета: что записалось, не известно — не записанным
                // считается всё, что ещё не отмечено.
                const reason = (e as Error)?.message || String(e);
                transient = true;
                allChanges.forEach((c) => {
                    if (!refused.has(keyOf(c))) refused.set(keyOf(c), reason);
                });
            }

            if (droppedPushes.length) {
                const where = droppedPushes
                    .map(({ change, reason }) =>
                        `${formatCueTime(
                            cueStartMs(subtitlesDataAllRef.current, [originalLang, change.lang], change.folderNum, change.trackId),
                        )} — ${reason}`,
                    )
                    .join("; ");
                setOverlapPushNotice(
                    `Сервер не принял машинный сдвиг (${where}): место реплики на диске не изменено, папка перечитана.`,
                );
                console.warn("[OVERLAP_PUSH] refused by the server, dropped:", where);
                // Перечитать можно только когда всё остальное легло: перечитывание не возвращает
                // несохранённые правки на экран.
                if (refused.size === 0) {
                    void getAllSubtitles();
                }
            }

            if (refused.size === 0) {
                refusedKeysRef.current = new Set();
                setRefusedEdits(0);
                // Прежнее «Не сохранено» больше не правда — снимаем его (чужие сообщения не трогаем).
                const stale = saveErrorShownRef.current;
                saveErrorShownRef.current = null;
                if (stale) setTranslatedOpError((prev) => (prev === stale ? null : prev));
                setAutoSaveStatus('saved');
                setTimeout(() => setAutoSaveStatus((prev) => prev === 'saved' ? 'idle' : prev), 2000);
                return;
            }

            // Не записанное возвращается в несохранённые — поверх него ложится то, что человек
            // успел поправить, пока запрос был в пути (новое поле побеждает старое, но старые
            // поля не теряются). Записанное обратно не возвращается и второй раз не уходит.
            // Отложенные машинные сдвиги (held) возвращаются тоже — но не в счёт отказов.
            const failedChanges = allChanges.filter((c) => refused.has(keyOf(c)));
            const requeued = [...failedChanges, ...held];
            refusedKeysRef.current = new Set(failedChanges.map(keyOf));
            setRefusedEdits(refusedKeysRef.current.size);
            setDirtyTracks((prev) => {
                const merged = new Map<string, DirtyTrackChange>();
                requeued.forEach((c) => merged.set(keyOf(c), c));
                prev.forEach((v, k) => {
                    const older = merged.get(k);
                    // Ключ записи несёт её происхождение (dirtyKeyOf): человек и машина не сливаются.
                    merged.set(
                        k,
                        older
                            ? {
                                  ...v,
                                  fields: { ...older.fields, ...v.fields },
                                  ...(older.liveTiming || v.liveTiming ? { liveTiming: true } : {}),
                              }
                            : v,
                    );
                });
                // Сдвиг из этого пакета устарел, если, пока запрос был в пути, человек сам
                // поставил ту же реплику (его новая запись меняет место), а нового сдвига нет.
                requeued.forEach((c) => {
                    if (!c.origin || prev.has(keyOf(c))) return;
                    const newer = prev.get(keyOf({ ...c, origin: undefined }));
                    if (newer && movesPlace(newer.fields)) merged.delete(keyOf(c));
                });
                dirtyTracksRef.current = merged;
                return merged;
            });
            setAutoSaveStatus('error');
            const message = saveFailureMessage(
                failedChanges.map((c) => ({
                    id: c.trackId,
                    atMs: cueStartMs(subtitlesDataAllRef.current, [originalLang, c.lang], c.folderNum, c.trackId),
                    reason: refused.get(keyOf(c)) as string,
                })),
            );
            saveErrorShownRef.current = message;
            setTranslatedOpError(message);
            console.error("[FLUSH] Auto-save refused:", message);
            // flushGate должен знать: следующая операция не начнётся. Что именно не записано — в
            // ошибке: «→» по реплике без пары может её заменить (translateSubtitle).
            throw saveRefusedError(message, { refused, transient });
        };

        localEditEpochRef.current += 1;
        const promise = doFlush();
        flushPromiseRef.current = promise;
        try {
            await promise;
        } finally {
            flushPromiseRef.current = null;
            localEditEpochRef.current += 1;
        }
    }, [mediaId, lang, originalLang, urlLang, getAllSubtitles]);

    const scheduleAutoSave = useCallback(() => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimerRef.current = setTimeout(() => {
            autoSaveTimerRef.current = null;
            flushDirtyChanges().catch((e) => console.error("[FLUSH] Auto-save flush error:", e));
        }, 2000);
    }, [flushDirtyChanges]);

    // `origin` — правку сделала машина (см. DirtyTrackChange.origin). У реплики две записи:
    // правка человека и машинный сдвиг, каждая под своим ключом (dirtyKeyOf), и они не сливаются:
    // сдвиг уходит своим помеченным запросом после строки человека, и журнал сервера пишет его
    // машине, а текст и место, поставленное человеком, — человеку. Человек сам поставил реплику
    // ПОСЛЕ сдвига — сдвиг устарел и снимается: ушёл бы следом и перезаписал его место.
    const markDirty = useCallback((
        trackLang: ILang,
        trackId: string,
        folderNum: number,
        fields: Partial<AudioTrackData>,
        origin?: DirtyTrackChange["origin"],
        liveTiming?: boolean,
    ) => {
        const change: DirtyTrackChange = {
            lang: trackLang,
            trackId,
            folderNum,
            fields,
            ...(origin ? { origin } : {}),
            ...(liveTiming ? { liveTiming: true } : {}),
        };
        const key = dirtyKeyOf(change);
        localEditEpochRef.current += 1;
        setDirtyTracks((prev: Map<string, DirtyTrackChange>) => {
            const next = new Map(prev);
            const existing = next.get(key);
            // Правка времени эфира (liveTiming) остаётся ею и после правки текста той же реплики:
            // иначе её место ушло бы прежним проводом — слотом, а сервер переписал бы длину дубля.
            next.set(
                key,
                existing
                    ? {
                          ...change,
                          fields: { ...existing.fields, ...fields },
                          ...(existing.liveTiming || change.liveTiming ? { liveTiming: true } : {}),
                      }
                    : change,
            );
            if (!origin && movesPlace(fields)) {
                next.delete(dirtyKeyOf({ ...change, origin: "manual_overlap_push" }));
            }
            dirtyTracksRef.current = next;
            return next;
        });
    }, []);

    // Flush gate: call before any Category B/C operation
    const flushGate = useCallback(async () => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        // Wait for any in-flight flush to complete first
        if (flushPromiseRef.current) {
            await flushPromiseRef.current;
        }
        // Then flush any remaining dirty tracks
        if (dirtyTracksRef.current.size > 0) {
            await flushDirtyChanges();
        }
    }, [flushDirtyChanges]);

    // Убрать из несохранённых правки, которые сервер не принял (по ключам грязной карты). Когда
    // непринятых не осталось, «Не сохранено» снимается.
    const dropRefusedEdits = useCallback((keys: string[]) => {
        const drop = new Set(keys);
        const without = (prev: Map<string, DirtyTrackChange>) => {
            const next = new Map(prev);
            drop.forEach((key) => next.delete(key));
            return next;
        };
        dirtyTracksRef.current = without(dirtyTracksRef.current);
        setDirtyTracks((prev) => {
            const next = without(prev);
            dirtyTracksRef.current = next;
            return next;
        });
        const remaining = new Set(Array.from(refusedKeysRef.current).filter((key) => !drop.has(key)));
        refusedKeysRef.current = remaining;
        setRefusedEdits(remaining.size);
        if (remaining.size === 0) {
            const stale = saveErrorShownRef.current;
            saveErrorShownRef.current = null;
            if (stale) setTranslatedOpError((prev) => (prev === stale ? null : prev));
            setAutoSaveStatus(dirtyTracksRef.current.size > 0 ? 'pending' : 'idle');
        }
    }, []);

    // Несохранённый текст перевода реплики — «→» спрашивает, прежде чем его заменить.
    const unsavedTranslation = useCallback(
        (trackId: string): string | undefined => dirtyTracksRef.current.get(`${lang}:${trackId}`)?.fields.captions,
        [lang],
    );

    // «Без автоправок — только ручная работа» — режим ПАРЫ, открытой на экране (флаг на сервере,
    // см. useManualOnly). Когда он включён, редактор не делает ничего, о чём его не просили:
    // правка одной реплики сохраняет её — и только те озвучки, на которые она наехала, сдвинутые
    // позже (правило 01.10.2026, см. cascadeNeighbours). Пока флаг не известен — всё как раньше.
    const manualOnlyPairLang =
        urlLang && urlLang !== originalLang ? urlLang : lang !== originalLang ? lang : "";

    // Перечитать папку пары за меркой дублей (см. dubMeasurePendingRef). Загрузка пары уже в пути
    // (открытие лекции: флаг пришёл раньше данных) — дождаться её: если она пришла от сервера,
    // знавшего о режиме, мерка уже в ней, и второй загрузки не нужно. Перед перечитыванием —
    // отложенные правки на сервер (так делает каждое перечитывание после операции): иначе
    // перечитанная папка легла бы поверх их места на экране.
    const rereadForDubMeasure = useCallback(async () => {
        const inFlight = pairReadInFlightRef.current;
        if (inFlight) {
            await inFlight;
            if (!dubMeasurePendingRef.current) return;
        }
        try {
            await flushGate();
        } catch (e) {
            // Отказ сохранения назван самим сохранением; папку перечитать всё равно нужно.
            console.warn("[OVERLAP_PUSH] сохранение перед перечитыванием за меркой дублей не удалось:", e);
        }
        if (!dubMeasurePendingRef.current) return;
        await getAllSubtitles();
    }, [flushGate, getAllSubtitles]);

    // Флаг пары сменился. Включился не загрузкой данных целиком — в данных нет мерки дублей:
    // раздвижка закрыта, пока папка не перечитана. Выключился — мерка из этого отрезка режима в
    // следующий не переносится: без режима переозвучка меняет дубли, а `dub_ms` в данных остаётся
    // прежним, поэтому следующее включение снова начнётся с перечитывания.
    const onManualOnlyFlagChange = useCallback(
        (on: boolean, source: ManualOnlyFlagSource) => {
            if (!on) {
                dubMeasurePendingRef.current = false;
                return;
            }
            if (source === "data") return;
            dubMeasurePendingRef.current = true;
            console.info(
                "[OVERLAP_PUSH] режим «Без автоправок» включён не загрузкой данных — папка перечитывается за меркой дублей (dub_ms); до неё раздвижка никого не толкает",
            );
            void rereadForDubMeasure();
        },
        [rereadForDubMeasure],
    );

    const manualOnlyControl = useManualOnly({
        mediaId,
        ready: fastApiReady && fastApiMediaId === mediaId,
        lang: manualOnlyPairLang ? wireLang(manualOnlyPairLang) : "",
        languageFrom: wireLang(originalLang),
        // Правки, сделанные до переключения, уходят на сервер по прежним правилам.
        beforeChange: flushGate,
        onFlagChange: onManualOnlyFlagChange,
    });
    const { manualOnlyRef } = manualOnlyControl;
    manualOnlyDataReadRef.current = manualOnlyControl.beginDataRead;
    // Живой эфир — по любому из трёх признаков: адрес редактора эфира, project_data
    // (`source: realtime_stream` — по нему решает и сервер) или статус эфира.
    const manualOnlyStream =
        isStreamEditor ||
        projectData?.source === "realtime_stream" ||
        Boolean(realtimeEditorStatus?.is_realtime_stream);
    // То же для колбэков (♪): живой эфир раскладывает озвучку сам, редактор соседей не двигает.
    const liveStreamRef = useRef(false);
    liveStreamRef.current = manualOnlyStream;

    // Правка времени на живом эфире у пары без «Без автоправок» (liveTiming.ts, проба P50m10d):
    // соседей по копии редактора не раздвигаем и не сохраняем, у самой реплики уходит только то,
    // что человек сдвинул или растянул. Ручная пара эфира — по своим правилам (cascadeNeighbours).
    const liveTimingNow = useCallback(() => liveStreamRef.current && !manualOnlyRef.current, [manualOnlyRef]);

    // Реплика в данных редактора (json пары) — от неё, а не от нарисованного, считается место.
    const storedRow = useCallback(
        (rowLang: ILang, folderNum: number, trackId: string): AudioTrackData | undefined =>
            subtitlesDataAllRef.current[rowLang]?.[folderNum - 1]?.translations.find((t) => t.id === trackId),
        [],
    );

    // Время перевода на эфире: на экран — место/длина самих реплик, в несохранённые — они же с
    // пометкой liveTiming (провод `stream_timing`); соседей никто не трогает.
    const saveLiveTiming = useCallback(
        (rowLang: ILang, folderNum: number, rows: { trackId: string; fields: Partial<AudioTrackData> }[]) => {
            if (!rows.length) return;
            setSubtitlesDataAll((prev) =>
                patchMultipleTracksInSubtitlesData(
                    prev,
                    rowLang,
                    folderNum,
                    rows.map(({ trackId, fields }) => ({ trackId, patch: fields })),
                ),
            );
            for (const { trackId, fields } of rows) markDirty(rowLang, trackId, folderNum, fields, undefined, true);
            scheduleAutoSave();
        },
        [markDirty, scheduleAutoSave],
    );

    // Колонка оригинала на эфире: правка — как прежде (слот русской реплики), но без раздвижки соседей.
    const saveOwnOnly = useCallback(
        (rowLang: ILang, folderNum: number, trackId: string, patch: Partial<AudioTrackData>) => {
            setSubtitlesDataAll((prev) => patchTrackInSubtitlesData(prev, rowLang, folderNum, trackId, patch));
            markDirty(rowLang, trackId, folderNum, patch);
            scheduleAutoSave();
        },
        [markDirty, scheduleAutoSave],
    );

    // Назвать сдвиги, сделанные машиной: переводчик должен видеть, что эту озвучку передвинул не
    // он. Время — реплики на дорожке (по оригиналу), как во всех сообщениях редактора. Полоса —
    // своя, нейтральная (overlapPushNotice): красная — для отказов сервера, и окно разбиения
    // показывало бы старое «Наложение снято…» как ошибку своей операции. Журнал сервера узнаёт о
    // сдвиге из пометки на его сохранении (DirtyTrackChange.origin).
    const announcePushes = useCallback(
        (
            moves: { id: string; from?: number | null; to?: number | null; by?: number; at: string }[],
            who: "editor" | "server",
        ) => {
            const notice = overlapPushNoticeText(moves);
            if (!notice) return;
            console.info(
                who === "server"
                    ? "[OVERLAP_PUSH] сервер снял наложение сдвигом позже (правило владельца 01.10.2026):"
                    : "[OVERLAP_PUSH] наложение снято сдвигом позже (правило владельца 01.10.2026):",
                moves.map(({ id, from, to, by }) => ({ id, from, to, by })),
            );
            setOverlapPushNotice(notice);
        },
        [],
    );

    // Сдвиги раздвижки редактора — по копии папки до сдвига.
    const namePushes = useCallback(
        (before: Partial<ISubtitlesDataAll>, pushedLang: ILang, folderNum: number, pushes: ShiftPatch[]) => {
            const was = new Map(
                (before[pushedLang]?.[folderNum - 1]?.translations || []).map((t) => [t.id, t.start]),
            );
            announcePushes(
                pushes.map(({ trackId, patch }) => ({
                    id: trackId,
                    from: was.get(trackId),
                    to: patch.start as number,
                    by: Math.round((patch.start as number) - (was.get(trackId) ?? (patch.start as number))),
                    at: formatCueTime(cueStartMs(before, [originalLang, pushedLang], folderNum, trackId)),
                })),
                "editor",
            );
        },
        [originalLang, announcePushes],
    );

    // Толчки, которые сервер ручной пары сделал сам и назвал в ответе (`pushed`, контракт C4), —
    // тем же текстом. Время — реплики на дорожке в переданных данных; нет её там (новый кусок
    // разбиения, а данные ещё старые) — её прежнее место по словам сервера.
    const nameServerPushes = useCallback(
        (pushes: OverlapPush[], pushedLang: ILang, folderNum: number, data: Partial<ISubtitlesDataAll>) => {
            const translations = data[pushedLang]?.[folderNum - 1]?.translations || [];
            announcePushes(
                pushes.map((push) => {
                    const local = translations.find((t) => t.id === push.id);
                    const atMs =
                        cueStartMs(data, [originalLang, pushedLang], folderNum, push.id) ??
                        (typeof push.from === "number" ? push.from : undefined);
                    return {
                        id: push.id,
                        from: push.from ?? local?.start,
                        to: push.start,
                        by: serverPushDeltaMs(push, local?.start),
                        at: formatCueTime(atMs),
                    };
                }),
                "server",
            );
        },
        [originalLang, announcePushes],
    );

    // Раздвижка соседей после правки (см. overlapCascade). Сдвинутые метятся грязными и уходят
    // на сервер автосохранением.
    //
    // Без режима «Без автоправок» — прежняя автоправка на любой колонке: всё, что ближе 50 мс к
    // предыдущей озвучке, уезжает вперёд.
    //
    // В режиме — правило владельца 01.10.2026: «ты должен всегда сдвигать соседние реплики иначе
    // будут наслоения». Снять наложение — не автоправка, оно снимается всегда, но иначе, чем
    // раскладкой: только на колонке ПАРЫ и только настоящее — озвучка, начавшаяся раньше конца
    // предыдущей, уезжает ПОЗЖЕ, на этот конец + 50 мс, дальше цепочкой, пока следующая наезжает
    // (в том числе сама правленая, если легла на предыдущую, и наезд, лежавший в папке с загрузки).
    // Раньше не двигается ничего, зазоры не закрываются, к русскому началу ничего не тянется,
    // скорость не подгоняется. Колонку оригинала в этом режиме двигает только человек. Каждый
    // такой сдвиг назван: на экране (namePushes), в консоли и в журнале сервера — сохраняется он
    // отдельным запросом с пометкой машинного (origin), если реплику не трогал человек.
    const cascadeNeighbours = useCallback(
        (data: Partial<ISubtitlesDataAll>, editLang: ILang, folderNum: number) => {
            // Озвучка без реплики оригинала молчит и соседей не толкает (sourceLang, см.
            // orphanVoicings.ts): иначе её место сохранялось бы как машинный сдвиг.
            if (!manualOnlyRef.current) {
                const { updated, shiftPatches } = cascadeOverlaps(data, editLang, folderNum, { sourceLang: originalLang });
                for (const sp of shiftPatches) {
                    markDirty(editLang, sp.trackId, folderNum, sp.patch);
                }
                return updated;
            }
            if (editLang === originalLang) return data;
            // Мерки дублей сервера в данных ещё нет (режим включили после загрузки, перечитанная
            // папка в пути — см. dubMeasurePendingRef): по json `length` толкать нельзя — это слот,
            // а не звук. Наезд, если он есть, снимет следующая правка — уже по мерке сервера.
            if (dubMeasurePendingRef.current) {
                console.info(
                    "[OVERLAP_PUSH] раздвижка отложена: папка перечитывается за меркой дублей (dub_ms) после включения режима",
                );
                return data;
            }
            const { updated, shiftPatches } = cascadeOverlaps(data, editLang, folderNum, {
                onlyOverlaps: true,
                sourceLang: originalLang,
            });
            if (!shiftPatches.length) return data;
            for (const sp of shiftPatches) {
                markDirty(editLang, sp.trackId, folderNum, sp.patch, "manual_overlap_push");
            }
            namePushes(data, editLang, folderNum, shiftPatches);
            // Сдвиг сохраняется сам: не все вызывающие сохраняют после раздвижки (переозвучка и
            // перечитывание после серверной озвучки решали это по грязной карте, которую React
            // мог ещё не обновить).
            scheduleAutoSave();
            return updated;
        },
        [markDirty, manualOnlyRef, originalLang, namePushes, scheduleAutoSave],
    );

    // Язык пары, открытой на экране (колонка озвучки), — или null, если пары нет.
    const pairLangNow = useCallback(
        (): ILang | null =>
            lang !== originalLang
                ? lang
                : urlLang && urlLang !== originalLang
                  ? (urlLang as ILang)
                  : null,
        [lang, originalLang, urlLang],
    );

    // После операций, где озвучку кладёт сервер (переозвучка ♪, проверенная озвучка, разбиение и
    // склейка перевода), — только в режиме «Без автоправок» и только когда сервер сам наложений не
    // снял (старый сервер или его проход не удался; новый называет свои толчки — см.
    // afterServerVoicing): наложений быть не должно (правило 01.10.2026). Раздвижка здесь — ТОЛЬКО
    // по перечитанной папке (`reread` — свежие данные легли) и мерой сервера (`dub_ms`, у старого —
    // `length`). По старой копии она ставила соседку не там, где лежит её дубль. Без режима
    // соседей раздвигает сам сервер — как раньше.
    const pushOverlapsAfterServerVoicing = useCallback(
        (folderNum: number, reread: boolean) => {
            if (!manualOnlyRef.current || !reread) return;
            const pairLang = pairLangNow();
            if (!pairLang) return;
            setSubtitlesDataAll((prev) => cascadeNeighbours(prev, pairLang, folderNum));
        },
        [manualOnlyRef, pairLangNow, cascadeNeighbours],
    );

    // Итог операции, где озвучку положил сервер (проверенная озвучка, разбиение и склейка
    // перевода), — после перечитывания папки. Новый сервер ручной пары сам прошёл папку и назвал
    // свои толчки (`pushed`, контракт C4): перечитанная папка уже такая, какой он её оставил, —
    // толчки только называются, раздвижка редактора не идёт (своей меркой и по своей копии она
    // ставила бы иначе, и автосохранение спорило бы с сервером). Не перечиталась папка — места,
    // названные сервером, кладутся на экран из ответа: иначе следующая правка шла бы от старых
    // мест. Поля нет (старый сервер) или проход на сервере не удался (`overlap_push_failed`) —
    // наложения снимает раздвижка, как раньше.
    const afterServerVoicing = useCallback(
        (folderNum: number, reread: boolean, response: unknown) => {
            const pushes = serverPushesOf(response);
            if (pushes === null) {
                pushOverlapsAfterServerVoicing(folderNum, reread);
                return;
            }
            const pairLang = pairLangNow();
            if (!pairLang) return;
            const before = subtitlesDataAllRef.current;
            if (!reread && pushes.length) {
                setSubtitlesDataAll((prev) =>
                    patchMultipleTracksInSubtitlesData(prev, pairLang, folderNum, serverPushPatches(pushes)),
                );
            }
            nameServerPushes(pushes, pairLang, folderNum, before);
        },
        [pushOverlapsAfterServerVoicing, pairLangNow, nameServerPushes],
    );

    // Время, которое нельзя отправлять (см. timingRefusal), не становится правкой: его причина
    // называется в полосе ошибок, а перетаскивание откатывается на месте.
    const acceptTiming = useCallback(
        (editedLang: ILang, trackId: string, start: number, length: number) => {
            const refusal = timingRefusal(editedLang === originalLang, start, length);
            if (!refusal) return true;
            setTranslatedOpError(
                `Время не сохранено: реплика ${formatCueTime(
                    cueStartMs(subtitlesDataAllRef.current, [originalLang, editedLang], folder, trackId),
                )} — ${refusal}`,
            );
            return false;
        },
        [originalLang, folder],
    );

    // beforeunload warning when dirty
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (dirtyTracks.size > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirtyTracks.size]);

    // Auto-flush on folder change + clean up previous folder's localStorage.
    // For a stream, advancing to the next folder means the departed folder is "done":
    // flush its edits, then regenerate that whole folder downstream (once, not per cue).
    useEffect(() => {
        if (folder !== prevFolderRef.current) {
            const departed = prevFolderRef.current;
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
            // Remove previous folder's localStorage data to prevent accumulation
            const prevKey = departed ? `${mediaId}_${departed}` : mediaId;
            if (prevKey) {
                localStorage.removeItem(prevKey);
            }
            prevFolderRef.current = folder;
            lastActiveSubtitleRef.current = null;
            lastActiveVoiceoverRef.current = null;
            void (async () => {
                // Save the departed folder's pending edits first (populates the accumulator
                // and writes the corrected Russian into the ru_ru master).
                try {
                    await flushGate();
                } catch (e) {
                    console.error("[folder-change] flush failed", e);
                }
            })();
        }
    }, [folder]);

    const editRusSubtitle = useCallback(
        async ({ track, ...editedPath }: IEditSubtitleArg) => {
            if (!mediaId) {
                return;
            }
            const trackId = track["original_id"];
            const folderNum = parseInt(track["folder_num"]);

            // Build patch from editedPath
            const patch: Partial<AudioTrackData> = {};
            if (editedPath.captions !== undefined) patch.captions = editedPath.captions;
            if (editedPath.start_rus_milli !== undefined) {
                patch.start = editedPath.start_rus_milli;
                patch.start_rus_milli = editedPath.start_rus_milli;
            }
            if (editedPath.end_rus_milli !== undefined) {
                patch.end_rus_milli = editedPath.end_rus_milli;
                if (editedPath.start_rus_milli !== undefined) {
                    patch.length = editedPath.end_rus_milli - editedPath.start_rus_milli;
                }
            }

            // Patch local state immediately
            setSubtitlesDataAll((prev) =>
                patchTrackInSubtitlesData(prev, originalLang, folderNum, trackId, patch)
            );

            // Mark dirty and schedule auto-save
            markDirty(originalLang, trackId, folderNum, patch);
            scheduleAutoSave();
            setEditingSubtitles(null);
        },
        [mediaId, lang, originalLang, urlLang, markDirty, scheduleAutoSave],
    );

    const editTranslatedSubtitle = useCallback(
        async ({
            captions,
            id,
            lang: subtitleLang,
            update_recommendation,
        }: Omit<EditTranslatedSubtitleArgs, "mediaId" | "folder_num">) => {
            // Patch local state immediately
            const patch: Partial<AudioTrackData> = {};
            if (captions !== undefined) patch.captions = captions;

            setSubtitlesDataAll((prev) =>
                patchTrackInSubtitlesData(prev, lang, folder, id, patch)
            );

            // Mark dirty and schedule auto-save
            markDirty(lang, id, folder, patch);
            scheduleAutoSave();
            setTranslatedSubtitleId(null);
        },
        [mediaId, folder, lang, markDirty, scheduleAutoSave],
    );

    const splitRusSubtitle = useCallback(
        async (body: Omit<SplitSubtitleArgs, "mediaId">) => {
            if (!mediaId) {
                return;
            }
            await flushGate();
            setWaitingCut((prev = []) => [...prev, body.id]);
            setSavingStatus(true);
            setSubtitleEditing(true);
            // Времена из запроса — равные доли окна, запасной вариант. `align: "words"`: сервер
            // ставит каждый кусок на его слова (раньше 37 % разбиений переводчик двигал руками).
            // `keep_translation`: перевод родителя режется по кускам, а не уходит в корзину.
            const data = await splitRusSubtitleAction({
                mediaId,
                ...body,
                align: "words",
                keep_translation: true,
                lang: wireLang(urlLang || lang),
                language_from: wireLang(originalLang),
            });

            let targetLang = lang;
            if (lang === originalLang && urlLang && urlLang !== originalLang) {
                targetLang = urlLang as ILang;
            }

            if (data.error) {
                setTranslatedOpError(`Реплика не разбита: ${data.error}`);
            }
            if (data.data) {
                await Promise.all([
                    deleteTranslatedSubtitleAction({
                        mediaId,
                        id: body.id,
                        lang: wireLang(targetLang),
                        folder_num: body.folder_num,
                        // Its sibling deleteAudioAction sent the source and this did not, so the
                        // backend fell back to "ru" and, on an en_ar or en_ch lecture, looked
                        // for the cue in a pair that lecture does not have.
                        language_from: wireLang(originalLang),
                    }),
                    deleteAudioAction({
                        mediaId,
                        folder_num: body.folder_num,
                        lang: wireLang(targetLang),
                        id: body.id,
                        language_from: wireLang(originalLang),
                    }),
                ]);
            }

            await getAllSubtitles();
            setSavingStatus(false);
            setSubtitleEditing(false);

            if (data.data) {
                const report = data.data as SplitReport;
                const notice = describeSplit(report, (ms) => formatCueTime(ms));
                if (notice) {
                    setTranslatedOpError(notice);
                }
                // Куски доделываются сами — перевод (если родительский не разрезался) и озвучка,
                // — как сделал бы переводчик «→» и ♪ на каждом. В паре «Без автоправок» — нет:
                // там это его работа.
                if (!manualOnlyRef.current && targetLang !== originalLang && report.created?.length) {
                    await completeSplitPiecesRef.current?.(report.created, targetLang);
                }
            }
        },
        [mediaId, getAllSubtitles, lang, urlLang, flushGate, originalLang, manualOnlyRef],
    );

    // «+»: реплика в промежутке [start; end]. Раньше ответ не читался вовсе: отказ («в
    // интервале нет речи», 404, 500) и успех выглядели одинаково — ничем, и переводчики жали
    // «+» снова, получая дубли. Теперь отказ назван в полосе ошибок, а новая реплика после
    // перечитывания папки выделяется и получает фокус (focusCue → SubtitlesBlock).
    const createRusSubInterval = async ({
        start,
        end,
    }: {
        start: number;
        end: number;
    }): Promise<{ meta_id?: string; error?: string; reason?: string }> => {
        try {
            await flushGate();
        } catch (e) {
            // Причину (какая правка не легла) полоса уже показывает.
            return { error: (e as Error)?.message || "не сохранились прежние правки" };
        }
        const targetFolder = folder;
        setSavingStatus(true);
        setSubtitleEditing(true);
        try {
            const result = await createRusSubIntervalAction({
                mediaId,
                folder_num: targetFolder,
                start_ms: start,
                end_ms: end,
                create_translate: false,
                language_translate: wireLang(urlLang || lang),
                create_synt_audio: false,
                language_from: wireLang(originalLang),
            });
            await getAllSubtitles();
            if (result.error) {
                setTranslatedOpError(
                    `Реплика не добавлена (${formatWindowTime(start)} – ${formatWindowTime(end)}): ${result.error}`,
                );
                return { error: result.error, reason: result.reason };
            }
            const metaId = result.data?.meta_id ? String(result.data.meta_id) : undefined;
            if (metaId) {
                setFocusCue({ id: metaId, folder: targetFolder, at: Date.now() });
            }
            return { meta_id: metaId };
        } finally {
            setSavingStatus(false);
            setSubtitleEditing(false);
        }
    };

    const createRusSubIntervalMulti = async ({
        start,
        end,
    }: {
        start: number;
        end: number;
    }): Promise<{ created_count: number; created_ids?: string[]; error?: string }> => {
        await flushGate();
        setSavingStatus(true);
        setSubtitleEditing(true);
        try {
            const result = await createRusSubIntervalMultiAction({
                mediaId,
                folder_num: folder,
                start_ms: start,
                end_ms: end,
                language_from: wireLang(originalLang),
                language_translate: wireLang(urlLang || lang),
            });
            if (result.error) {
                return { created_count: 0, error: result.error };
            }
            await getAllSubtitles();
            return {
                created_count: result.data?.created_count || 0,
                created_ids: (result.data?.created_ids || []).map(String),
            };
        } catch (err) {
            return { created_count: 0, error: (err as Error).message };
        } finally {
            setSavingStatus(false);
            setSubtitleEditing(false);
        }
    };

    const deleteAudio = useCallback(
        async ({ lang, id }: { lang: MetadataToSubtitlesLanguages; id: string }) => {
            await flushGate();
            const data = await deleteAudioAction({
                mediaId,
                folder_num: folder,
                lang,
                id,
                language_from: wireLang(originalLang),
            });
            if (data.error) {
                setTranslatedOpError(`Озвучка не удалена: ${data.error}`);
            }
            await getAllSubtitles();
            return data;
        },
        [mediaId, folder, getAllSubtitles],
    );

    const deleteTranslatedSubtitle = useCallback(
        async ({ id, selectLang }: { id: string; selectLang: ILang }) => {
            // Перевод куска фразы — его своя часть перевода и озвучки, а не дубль. Раньше это
            // удаление не спрашивало ничего; обычная реплика удаляется сразу, как прежде.
            const piece = phrasePieceOf(translationTrackAllRef.current[originalLang], id);
            if (piece && !window.confirm(phrasePieceDeleteQuestion([piece], 1, getUiLang() === "en"))) {
                return;
            }
            await flushGate();
            setSavingStatus(true);
            setSubtitleEditing(true);
            const deleted: any = await deleteTranslatedSubtitleAction({
                mediaId,
                id: id,
                lang: wireLang(selectLang),
                folder_num: folder,
                language_from: wireLang(originalLang),
            });
            if (deleted && typeof deleted.error === "string") {
                setTranslatedOpError(`Перевод не удалён: ${deleted.error}`);
            }
            await getAllSubtitles();
            setSavingStatus(false);
            setSubtitleEditing(false);
        },
        [mediaId, lang, folder, getAllSubtitles, originalLang],
    );

    // 🗑 на озвучке без реплики оригинала (4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026:
    // «при удалении отрывка на оригинале озвучка на испанском осталась. Не могу найти как её
    // удалить»). Строки у такой озвучки нет, и ни одна кнопка редактора её не убирала. Здесь —
    // вопрос с её временем и текстом, затем json перевода и его wav в паре (та же пара запросов,
    // что убирает перевод разбитой реплики) и перечитывание папки. Отказ — словами сервера в
    // красной полосе; папка перечитывается и тогда: экран показывает то, что на диске.
    const deleteOrphanVoicing = useCallback(
        async (track: AudioTrack): Promise<{ error?: string }> => {
            const pairLang = pairLangNow();
            if (!mediaId || !pairLang) return {};
            const en = getUiLang() === "en";
            const naming = {
                startMs: typeof track.start_rus_milli === "number" ? track.start_rus_milli : track.start,
                text: track.captions,
                sourceLang: originalLang,
            };
            if (!window.confirm(orphanDeleteQuestion(naming, en))) return {};
            try {
                await flushGate();
            } catch (e) {
                const message = orphanDeleteRefusal(naming, (e as Error)?.message || "не сохранились прежние правки", en);
                setTranslatedOpError(message);
                return { error: message };
            }
            setSavingStatus(true);
            setSubtitleEditing(true);
            let failure: string | null = null;
            try {
                failure = await removeOrphanVoicing({
                    mediaId,
                    folder,
                    id: track.original_id,
                    lang: wireLang(pairLang),
                    languageFrom: wireLang(originalLang),
                });
                await getAllSubtitles();
            } finally {
                setSavingStatus(false);
                setSubtitleEditing(false);
            }
            if (failure) {
                const message = orphanDeleteRefusal(naming, failure, en);
                setTranslatedOpError(message);
                return { error: message };
            }
            return {};
        },
        [mediaId, folder, originalLang, pairLangNow, flushGate, getAllSubtitles],
    );

    // Машинный перевод одной реплики («→» или перевод склеенной). Текст переводчика сервер
    // заменяет только по overwrite, который шлётся после подтверждения человеком; отказ
    // («текст уже есть», «без автоправок», словарь недоступен) назван в полосе ошибок.
    const translateSubtitle = useCallback(
        async ({
            toLang,
            fromLang,
            trackId,
            overwrite,
            overrideManualOnly,
            quietIfExists,
        }: TranslateSubtitleRequest): Promise<{ error?: string; reason?: string }> => {
            // Несохранённый текст ложится первым: иначе автосохранение через две секунды
            // записало бы его поверх только что полученного перевода.
            try {
                await flushGate();
            } catch (e) {
                // Единственный отказ — текст ЭТОЙ реплики, у которой нет пары: сохранить его
                // нельзя (старому серверу некуда писать), а «→» пару и создаёт. Текст пуст или
                // человек подтвердил его замену — правка снимается, и перевод идёт. Иначе стоп.
                const refusal = saveRefusalOf(e);
                const key = `${lang}:${trackId}`;
                const pending = dirtyTracksRef.current.get(key);
                const pairless = !subtitlesDataAllRef.current[lang]?.[folder - 1]?.translations.some(
                    (t) => t.id === trackId,
                );
                const supersedes =
                    Boolean(refusal) &&
                    !refusal!.transient &&
                    refusal!.refused.size === 1 &&
                    refusal!.refused.has(key) &&
                    pairless &&
                    Boolean(pending) &&
                    Object.keys(pending!.fields).every((field) => field === "captions") &&
                    (overwrite || !(pending!.fields.captions || "").trim());
                if (!supersedes) {
                    return { error: (e as Error)?.message || "не сохранились прежние правки" };
                }
                dropRefusedEdits([key]);
            }
            setSavingStatus(true);
            setTranslatedSubtitleId(trackId);
            setEditedTrackId(folder + "_" + trackId);
            try {
                const result = await translateSubtitleAction({
                    mediaId,
                    folder_num: folder,
                    id: trackId,
                    language_from: fromLang,
                    language_to: toLang,
                    ...(overwrite ? { overwrite: true } : {}),
                    ...(overrideManualOnly ? { override_manual_only: true } : {}),
                });
                if (result.error && !(quietIfExists && result.reason === "translation_exists")) {
                    setTranslatedOpError(
                        `Машинный перевод не сделан: реплика ${formatCueTime(
                            cueStartMs(subtitlesDataAllRef.current, [originalLang, lang], folder, trackId),
                        )} — ${result.error}`,
                    );
                }
                await getAllSubtitles();
                return result.error ? { error: result.error, reason: result.reason } : {};
            } finally {
                setSavingStatus(false);
                setTranslatedSubtitleId(null);
                // Метка «переозвучивается» не остаётся висеть: раньше её не снимал никто, и
                // расписание ждало эту реплику, а Alt+Space по ней молчал.
                setEditedTrackId(null);
            }
        },
        [mediaId, folder, getAllSubtitles, flushGate, originalLang, lang, dropRefusedEdits],
    );

    // «Сбросить несохранённое» — выход из отказа, который иначе запирал редактор: каждая
    // операция сначала сохраняет несохранённое, и непринятая правка роняла её до перезагрузки
    // (а перезагрузка правку теряла молча). Человек видит, что именно будет потеряно.
    const discardRefusedEdits = useCallback(async (): Promise<boolean> => {
        const keys = Array.from(refusedKeysRef.current).filter((key) => dirtyTracksRef.current.has(key));
        if (!keys.length) {
            dropRefusedEdits(Array.from(refusedKeysRef.current));
            return false;
        }
        const short = (text: string) => (text.length > 120 ? `${text.slice(0, 120)}…` : text);
        const lines = keys.slice(0, 5).map((key) => {
            const change = dirtyTracksRef.current.get(key)!;
            const at = formatCueTime(
                cueStartMs(subtitlesDataAllRef.current, [originalLang, change.lang], change.folderNum, change.trackId),
            );
            const what =
                change.fields.captions !== undefined ? `«${short(change.fields.captions)}»` : "время реплики";
            return `• ${at} — ${what}`;
        });
        const more = keys.length > 5 ? `\n… и ещё ${keys.length - 5}` : "";
        const question =
            `Сбросить несохранённые правки (${keys.length})? Сервер их не принял; они будут потеряны:\n\n` +
            `${lines.join("\n")}${more}`;
        if (!window.confirm(question)) {
            return false;
        }
        dropRefusedEdits(keys);
        // Остальное несохранённое ложится как обычно; потом папка перечитывается — на экране то,
        // что на сервере. Новый отказ остаётся на экране, и папка тогда не перечитывается.
        try {
            await flushGate();
        } catch (e) {
            return true;
        }
        await getAllSubtitles();
        return true;
    }, [dropRefusedEdits, flushGate, getAllSubtitles, originalLang]);

    const mergeCaptions = useCallback(
        async (track: AudioTrack) => {
            if (!mediaId) {
                return;
            }
            if (trackSelected === track.original_id) {
                setTrackSelected(null);
                return;
            }
            if (!trackSelected) {
                setTrackSelected(track.original_id);
                return;
            }
            // Склейка куска фразы — не удаление дубля: переводы частей встанут подряд, озвучки
            // сведутся в одну, а общую русскую фразу сервер может поставить подряд дважды. Кусок-
            // повтор (перевод как у соседа) назван повтором: его удаляют, а не склеивают. Отказ
            // снимает выбор — следующий клик снова начинает склейку.
            const sourceRows = translationTrackAllRef.current[originalLang];
            const mergeQuestion = phrasePieceMergeQuestion(
                phrasePieceOf(sourceRows, trackSelected),
                phrasePieceOf(sourceRows, track.original_id),
                getUiLang() === "en",
                "source",
            );
            if (mergeQuestion && !window.confirm(mergeQuestion)) {
                setTrackSelected(null);
                return;
            }
            await flushGate();

            const newJsonData = {
                id: [trackSelected, track.original_id],
                folder_num: parseInt(track["folder_num"]),
            };

            setSavingStatus(true);
            if (newJsonData) {
                setSubtitleEditing(true);

                let targetLang = track.lang;
                if (urlLang) {
                    // If we are merging source tracks (ru) but in isolated context (ru_en),
                    // we must pass the target lang (en-US) so backend finds the correct ru_en folder.
                    targetLang = wireLang(urlLang);
                }

                const data = await mergeSubtitles({
                    mediaId,
                    lang: targetLang, // Use the corrected target lang
                    ...newJsonData,
                    language_from: wireLang(originalLang),
                });



                if (data.error) {
                    setTranslatedOpError(`Реплики не склеены: ${data.error}`);
                }
                if (data.data) {
                    // Новый сервер присылает склеенный перевод; старый о нём молчит (undefined).
                    const mergedTarget = mergedTargetFor(
                        data.data,
                        pairLangKeys(urlLang || lang, String(targetLang)),
                    );
                    const mergedHasText =
                        typeof mergedTarget?.captions === "string" && mergedTarget.captions.trim() !== "";
                    if (manualOnlyRef.current || mergedHasText) {
                        // Без автоправок — или сервер склеил текст переводчика: машинный перевод
                        // поверх него не ставится. Только перечитываем папку, как сделал бы перевод.
                        await getAllSubtitles();
                    } else {
                        translateSubtitle({
                            toLang: targetLang,
                            fromLang: originalLang,
                            // The merge endpoint answers {merged: {...}} — the flat id was undefined.
                            trackId: (data.data as any)?.merged?.id ?? (data.data as any)?.id,
                            // Без overwrite: если у склеенной реплики уже есть текст, новый сервер
                            // откажет «текст уже есть» — это не ошибка, текст переводчика остаётся.
                            quietIfExists: true,
                        });
                    }
                }
                setTrackSelected(null);
                setSavingStatus(false);
                setSubtitleEditing(false);
            }
        },
        [mediaId, trackSelected, lang, translateSubtitle, originalLang, urlLang, getAllSubtitles, manualOnlyRef],
    );

    // --- Translated-led split & merge -------------------------------------------------
    // The translated text drives the operation; the backend owns the whole lifecycle
    // (RU counterparts, TTS, boundary snapping) — no follow-up delete calls here.

    const clearTranslatedOpError = useCallback(() => setTranslatedOpError(null), []);
    const clearOverlapPushNotice = useCallback(() => setOverlapPushNotice(null), []);
    // Та же красная полоса над дорожками — для отказов, которые ловят сами компоненты.
    const showEditorError = useCallback((message: string | null) => setTranslatedOpError(message), []);

    const splitTranslatedSubtitle = useCallback(
        async ({
            id,
            folder_num,
            translated_parts,
        }: {
            id: string;
            folder_num: number;
            translated_parts: string[];
        }) => {
            if (!mediaId) {
                return;
            }
            await flushGate();
            setWaitingCut((prev = []) => [...prev, id]);
            setSavingStatus(true);
            setSubtitleEditing(true);
            // Сервер разрезал и озвучил куски (ответ без отказа) — после перечитывания их наезды
            // сняты им самим или снимаются здесь (afterServerVoicing). Отказ или «нет» на вопрос —
            // папку сервер не менял, трогать нечего.
            let laid = false;
            let answer: unknown;
            try {
                const args = {
                    mediaId,
                    id,
                    folder_num,
                    translated_parts,
                    lang: wireLang(urlLang || lang),
                    language_from: wireLang(originalLang),
                };
                let result: { data?: unknown; error?: string } = await splitTranslatedSubtitleAction(args);
                // The hand-over warning (owner's model): the Russian of this folder was not
                // explicitly marked finished — confirm takes the lead and locks the folder.
                if (result.error && result.error.includes("не помечен завершённым")) {
                    if (window.confirm(result.error)) {
                        result = await splitTranslatedSubtitleAction({
                            ...args,
                            confirm_translation_lead: true,
                        });
                    } else {
                        result = {};
                    }
                }
                if (result.error) {
                    setTranslatedOpError(result.error);
                }
                laid = !result.error && "data" in result;
                answer = result.data;
            } finally {
                const reread = await getAllSubtitles();
                if (laid) afterServerVoicing(folder_num, reread, answer);
                // On a 409/422 the cue still exists — never leave it stuck in "Wait...".
                setWaitingCut((prev) => prev.filter((one) => one !== id));
                setSavingStatus(false);
                setSubtitleEditing(false);
            }
        },
        [mediaId, getAllSubtitles, lang, urlLang, originalLang, flushGate, afterServerVoicing],
    );

    const opusSplitTranslatedSubtitle = useCallback(
        async ({ id, folder_num }: { id: string; folder_num: number }) => {
            if (!mediaId) {
                return;
            }
            await flushGate();
            // Opus + N TTS calls — slow, so mark the cue itself busy the way
            // audioSynthesizeWithCheck does.
            setSavingStatus(true);
            setTranslatedSubtitleId(id);
            setEditedTrackId(folder_num + "_" + id);
            // Как у разбиения: куски озвучил сервер — их наезды сняты им или снимаются после
            // перечитывания (afterServerVoicing).
            let laid = false;
            let answer: unknown;
            try {
                const args = {
                    mediaId,
                    id,
                    folder_num,
                    lang: wireLang(urlLang || lang),
                    language_from: wireLang(originalLang),
                };
                let result: { data?: unknown; error?: string } = await opusSplitTranslatedSubtitleAction(args);
                if (result.error && result.error.includes("не помечен завершённым")) {
                    if (window.confirm(result.error)) {
                        result = await opusSplitTranslatedSubtitleAction({
                            ...args,
                            confirm_translation_lead: true,
                        });
                    } else {
                        result = {};
                    }
                }
                if (result.error) {
                    setTranslatedOpError(result.error);
                }
                laid = !result.error && "data" in result;
                answer = result.data;
            } finally {
                const reread = await getAllSubtitles();
                if (laid) afterServerVoicing(folder_num, reread, answer);
                setEditedTrackId(null);
                setTranslatedSubtitleId(null);
                setSavingStatus(false);
            }
        },
        [mediaId, getAllSubtitles, lang, urlLang, originalLang, flushGate, afterServerVoicing],
    );

    const mergeTranslatedCaptions = useCallback(
        async (track: AudioTrack) => {
            if (!mediaId) {
                return;
            }
            if (translatedTrackSelected === track.original_id) {
                setTranslatedTrackSelected(null);
                return;
            }
            if (!translatedTrackSelected) {
                setTranslatedTrackSelected(track.original_id);
                return;
            }
            // Как у склейки в колонке оригинала: кусок фразы назван, с его частью перевода. Эта
            // склейка озвучивает склеенный перевод заново и ставит русские тексты частей подряд —
            // вопрос говорит и это.
            const sourceRows = translationTrackAllRef.current[originalLang];
            const mergeQuestion = phrasePieceMergeQuestion(
                phrasePieceOf(sourceRows, translatedTrackSelected),
                phrasePieceOf(sourceRows, track.original_id),
                getUiLang() === "en",
                "translation",
            );
            if (mergeQuestion && !window.confirm(mergeQuestion)) {
                setTranslatedTrackSelected(null);
                return;
            }
            await flushGate();
            setSavingStatus(true);
            setSubtitleEditing(true);
            try {
                const args = {
                    mediaId,
                    id: [translatedTrackSelected, track.original_id],
                    folder_num: parseInt(track["folder_num"]),
                    lang: wireLang(urlLang || lang),
                    language_from: wireLang(originalLang),
                };
                let result: { data?: unknown; error?: string } = await mergeTranslatedSubtitlesAction(args);
                if (result.error && result.error.includes("не помечен завершённым")) {
                    if (window.confirm(result.error)) {
                        result = await mergeTranslatedSubtitlesAction({
                            ...args,
                            confirm_translation_lead: true,
                        });
                    } else {
                        result = {};
                    }
                }
                if (result.error) {
                    setTranslatedOpError(result.error);
                } else {
                    // No translateSubtitle call: the backend merged the translated text itself.
                    const reread = await getAllSubtitles();
                    // Склеенную озвучку положил сервер — её наезд на следующие снят им самим или
                    // снимается сдвигом позже здесь (только без автоправок; см. afterServerVoicing).
                    if ("data" in result) afterServerVoicing(args.folder_num, reread, result.data);
                }
            } finally {
                setTranslatedTrackSelected(null);
                setSavingStatus(false);
                setSubtitleEditing(false);
            }
        },
        [mediaId, translatedTrackSelected, getAllSubtitles, lang, urlLang, originalLang, flushGate, afterServerVoicing],
    );

    const editMasterRuSubtitle = useCallback(
        async ({
            id,
            folder_num,
            captions,
        }: {
            id: string;
            folder_num: number;
            captions: string;
        }) => {
            if (!mediaId) {
                return;
            }
            await flushGate();
            const result = await editMasterRuSubtitleAction({
                mediaId,
                id,
                folder_num,
                captions,
                lang: wireLang(urlLang || lang),
                language_from: wireLang(originalLang),
            });
            if (result.error) {
                setTranslatedOpError(result.error);
            }
            await getAllSubtitles();
            return result;
        },
        [mediaId, getAllSubtitles, lang, urlLang, originalLang, flushGate],
    );

    const recreateFromRu = useCallback(
        async ({ dry_run }: { dry_run: boolean }) => {
            return recreateTranslatedFromRuAction({
                mediaId,
                folder_num: folder,
                lang: wireLang(urlLang || lang),
                language_from: wireLang(originalLang),
                dry_run,
            });
        },
        [mediaId, folder, lang, urlLang, originalLang],
    );

    // «Русский готов» — the explicit per-folder mark (owner's model); read + toggle.
    const getRuDone = useCallback(async () => {
        if (!mediaId) return { error: "no media" };
        return ruDoneGetAction(mediaId);
    }, [mediaId]);

    const setRuDone = useCallback(
        async ({ folder_num, done }: { folder_num: number; done: boolean }) => {
            if (!mediaId) return { error: "no media" };
            return ruDoneSetAction({ mediaId, folder_num, done });
        },
        [mediaId],
    );

    // «Папка готова» — the translator hands the folder in; the backend records it and asks the
    // console's queue for воицing → финальное видео → публикация. `lang` is the OPEN language,
    // never a default: handing in a folder of the wrong pair would re-render someone else's.
    const getTranslationDone = useCallback(async () => {
        if (!mediaId) return { error: "no media" };
        if (!toLang) return { error: "не выбран язык перевода" };
        return translationDoneGetAction(mediaId, toLang, originalLang);
    }, [mediaId, toLang, originalLang]);

    const setTranslationDone = useCallback(
        async ({
            folder_num,
            done,
            run,
            verify,
        }: {
            folder_num: number;
            done: boolean;
            run?: boolean;
            verify?: boolean;
        }) => {
            if (!mediaId) return { error: "no media" };
            if (!toLang) return { error: "не выбран язык перевода" };
            if (manualOnlyRef.current) {
                // Без автоправок сдаётся ровно работа переводчика — значит, и последняя его
                // правка, ещё ждущая автосохранения, должна лечь на диск ДО того, как сборка
                // прочтёт папку. Не легла — не сдаём: собралась бы папка без неё.
                try {
                    await flushGate();
                } catch (e) {
                    return {
                        error: "Не удалось сохранить последние правки — папка не сдана. Повторите сдачу.",
                    };
                }
            }
            return translationDoneSetAction({
                mediaId,
                folder_num,
                done,
                run,
                verify,
                lang: toLang,
                language_from: originalLang,
            });
        },
        [mediaId, toLang, originalLang, flushGate, manualOnlyRef],
    );

    const getNextTrackId = (
        // TODO remove from context to component
        currentTrackId: string | null,
        tracks: AudioTrack[] = translationTrackAll[originalLang] || [],
    ) => {
        const currentIndex = tracks.findIndex((track) => track.id === currentTrackId);
        if (currentIndex === tracks.length - 1 || currentIndex === -1) {
            return null;
        } else {
            return tracks[currentIndex + 1].id;
        }
    };

    const deleteFragment = (oneTrack: AudioTrack) => {
        return; // TODO move to new api

        const isConfirmed = window.confirm("Are you sure you want to delete this fragment?");
        if (isConfirmed) {
            let jsonData = {
                id: oneTrack["original_id"],
                // file: oneTrack['file'],
                folder_num: oneTrack["folder_num"],
                lang: oneTrack["lang"],
                //lang: lang
            };
            const jsonString = JSON.stringify(jsonData, null, 2);
            // jsonPreview(jsonString)
            console.log("Sending this (deleting 🔴) = ", jsonString);
            setChanged((prev) => prev.filter((one) => one !== oneTrack["id"]));
            setSavingStatus(true);
            // jsonString &&
            //   postData(`${API_URL}/delete_tts?media=${mediaId}`, jsonString).then((data) => {
            //     setSavingStatus(false);
            //     console.log("Server: ", data); // JSON data parsed by `response.json()` call
            //     // TODO await
            //     void getAndSaveTracks(mediaId, () => {
            //       showSubtitleEditor();
            //       scrollToElement(getPrevTrackId(oneTrack["id"], translationData));
            //     });
            //   });
        } else {
            // Do nothing if not confirmed
            console.log("Deletion cancelled.");
        }
    };

    const deleteSubtitle = useCallback(
        async (oneTrack: AudioTrack) => {
            // Кусок фразы (окно правки реплики): вопрос называет его куском, а не дублем, с его
            // частью перевода; обычная реплика — прежний вопрос.
            const piece = phrasePieceOf(translationTrackAllRef.current[originalLang], oneTrack.original_id);
            const isConfirmed = window.confirm(
                piece
                    ? phrasePieceDeleteQuestion([piece], 1, getUiLang() === "en")
                    : "Are you sure you want to delete this fragment?",
            );
            if (isConfirmed) {
                await flushGate();
                let jsonData = {
                    id: oneTrack.original_id,
                    folder_num: parseInt(oneTrack.folder_num),
                    ru_only: false,
                };
                setChanged((prev) => prev.filter((one) => one !== oneTrack["id"]));
                setSavingStatus(true);
                setSubtitleEditing(true);

                let targetLang = wireLang(lang);
                if (lang === originalLang && urlLang && urlLang !== originalLang) {
                    targetLang = wireLang(urlLang);
                }

                const data: any = await deleteRusSubtitleAction({
                    mediaId,
                    ...jsonData,
                    lang: targetLang,
                    language_from: wireLang(originalLang),
                    deleted_lang: originalLang,
                });
                if (data && typeof data.error === "string") {
                    setTranslatedOpError(`Реплика не удалена: ${data.error}`);
                }
                await getAllSubtitles();
                setSavingStatus(false);
                setSubtitleEditing(false);

                showSubtitleEditor();

                scrollToElement(
                    getPrevTrackId(oneTrack["id"], translationTrackAll[originalLang] || []),
                );
            } else {
                // Do nothing if not confirmed
                console.log("Deletion cancelled.");
            }
        },
        [
            mediaId,
            showSubtitleEditor,
            scrollToElement,
            translationTrackAll,
            getAllSubtitles,
            originalLang,
            urlLang,
        ],
    );

    // `confirmed` — удаление уже подтверждено в диалоге вызывающего (замена по интервалу), второй
    // вопрос не задаётся. Отказ сервера по любой реплике назван в полосе ошибок и возвращается.
    const deleteSubtitles = useCallback(
        async (trackIds: string[], options?: { confirmed?: boolean }): Promise<{ error?: string }> => {
            // Среди выбранных куски фраз: общий вопрос не говорил, что удаляется часть перевода и
            // её звук, и 01.10.2026 переводчица удалила 10 кусков как дубли (4119e839, папка 21).
            const pieces = trackIds
                .map((id) => phrasePieceOf(translationTrackAllRef.current[originalLang], id))
                .filter((piece): piece is NonNullable<typeof piece> => Boolean(piece));
            const isConfirmed =
                options?.confirmed ||
                window.confirm(
                    pieces.length
                        ? phrasePieceDeleteQuestion(pieces, trackIds.length, getUiLang() === "en")
                        : "Are you sure you want to delete selected fragment?",
                );
            if (isConfirmed) {
                try {
                    await flushGate();
                } catch (e) {
                    return { error: (e as Error)?.message || "не сохранились прежние правки" };
                }
                const tracksData = trackIds.map((track) => ({
                    id: track,
                    folder_num: folder,
                    ru_only: false,
                }));
                setSavingStatus(true);
                setSubtitleEditing(true);
                const promises: Array<any> = [];

                let targetLang = wireLang(lang);
                if (lang === originalLang && urlLang && urlLang !== originalLang) {
                    targetLang = wireLang(urlLang);
                }

                tracksData.forEach((track) => {
                    setChanged((prev) => prev.filter((one) => one !== track.id));
                    promises.push(
                        deleteRusSubtitleAction({
                            mediaId,
                            ...track,
                            lang: targetLang,
                            language_from: wireLang(originalLang),
                            deleted_lang: originalLang,
                        }),
                    );
                });

                const results = await Promise.all(promises);
                const failed = results
                    .map((res: any, i: number) =>
                        res && typeof res.error === "string" ? `${tracksData[i].id}: ${res.error}` : null,
                    )
                    .filter(Boolean);

                await getAllSubtitles();
                setSavingStatus(false);
                setSubtitleEditing(false);

                showSubtitleEditor();
                if (failed.length) {
                    const message = `Не удалены реплики: ${failed.join("; ")}`;
                    setTranslatedOpError(message);
                    return { error: message };
                }
            } else {
                // Do nothing if not confirmed
                console.log("Deletion cancelled.");
            }
            return {};
        },
        [
            mediaId,
            showSubtitleEditor,
            scrollToElement,
            translationTrackAll,
            getAllSubtitles,
            originalLang,
        ],
    );

    const [waitingCut, setWaitingCut] = useState<string[]>([]);

    useEffect(() => {
        saveToLocal();
    }, [changed, changedTranslation, changedRus]);

    const getRusAudio = async (mediaId: string, folder: number) => {
        if (mediaFolderRef.current.mediaId !== mediaId || mediaFolderRef.current.folder !== folder) return;
        setMp3(undefined);
        pendingMp3Ref.current = null;
        const { data, error } = await getDownloadedRusAudioUrl(
            `${mediaId}/files_to_send/${folder}.mp3`,
        );
        if (data && mediaFolderRef.current.mediaId === mediaId && mediaFolderRef.current.folder === folder) {
            setMp3(data);
        }
    };

    useEffect(() => {
        if (playingFragment) {
            saveToLocal();
        }
    }, [playingFragment]);

    useEffect(() => {
        if (trackSelected) {
            alert("Select a second fragment to merge with.");
        }
    }, [trackSelected]);

    const getAndSaveFolders = async (mediaId: string) => {
        setFoldersTotal(1);
        const response = await getFoldersAction(mediaId);
        if (response.data) {
            setFoldersTotal(response.data["numPages"]);
        }
        if (response.error) {
            // TODO save and show error
        }
    };

    const refreshRealtimeEditorStatus = useCallback(async () => {
        // Never call the stream-editor namespace for a lecture. The initial
        // session probe above is the only cross-check and must explicitly
        // confirm `source: realtime_stream` before this path is enabled.
        if (!mediaId || !fastApiReady || !isStreamEditor) return;
        const status = await getRealtimeEditorStatusApi(mediaId);
        setRealtimeEditorStatus(status);
        if (!status.is_realtime_stream) return;

        const folders = status.folders || [];
        const maxFolder = folders.reduce((max, item) => Math.max(max, item.folder_num || 0), 0);
        if (maxFolder > 0) {
            setFoldersTotal(maxFolder);
        }
        const activeFolder = folders.find((item) => item.folder_num === folder);
        const realtimeLang = lang === "ch" ? "zh" : lang;
        const activeLanguage = activeFolder?.languages?.[realtimeLang];
        const signature = activeLanguage
            ? [
                activeLanguage.revision,
                activeLanguage.subtitles_ready,
                activeLanguage.audio_ready,
                activeLanguage.video_ready,
                activeLanguage.hls_published,
            ].join(":")
            : null;
        const activeFolderLoaded = Boolean(
            subtitlesDataAll[lang]?.some((item: any) => Number(item?.folder_num) === Number(folder)),
        );
        const needsReload = Boolean(
            signature
            && (!activeFolderLoaded
                || (realtimeFolderSignatureRef.current
                    && realtimeFolderSignatureRef.current !== signature)),
        );
        if (needsReload) {
            // NEVER WHILE IT IS PLAYING.
            //
            // On a live folder these flags change constantly -- the incremental
            // Russian bumps `revision` as each cue lands -- so this fires every
            // few seconds. getAllSubtitles() replaces the editor's data, which
            // rebuilds the tracks and the waveform and drops the playhead back
            // to the start of the folder. Listening to a folder while the stream
            // was still running was therefore impossible: playback restarted
            // from the beginning every time, and the waveform disappeared with
            // it.
            //
            // The new cues are still wanted, just not in the middle of someone
            // listening. Park it and apply it the moment playback stops.
            if (playingRef.current) {
                pendingRealtimeReloadRef.current = true;
            } else {
                pendingRealtimeReloadRef.current = false;
                await getAllSubtitles();
            }
        }
        realtimeFolderSignatureRef.current = signature;
    }, [mediaId, fastApiReady, isStreamEditor, folder, lang, getAllSubtitles, subtitlesDataAll]);

    useEffect(() => {
        if (!mediaId || offline || !fastApiReady || !isStreamEditor) return;
        void refreshRealtimeEditorStatus().catch((error) => {
            console.warn("Could not load realtime editor status", error);
        });
    }, [mediaId, fastApiReady, isStreamEditor, refreshRealtimeEditorStatus]);

    useEffect(() => {
        if (!mediaId || !realtimeEditorStatus?.is_realtime_stream) return;
        const intervalId = window.setInterval(() => {
            void refreshRealtimeEditorStatus().catch((error) => {
                console.warn("Could not refresh realtime editor status", error);
            });
        }, 15_000);
        return () => window.clearInterval(intervalId);
    }, [mediaId, realtimeEditorStatus?.is_realtime_stream, refreshRealtimeEditorStatus]);

    const fetchVideoUrl = useCallback(() => {
        const index = folder - 1;
        if (subtitlesDataAll[originalLang]?.[index]) {
            setVideoUrl(subtitlesDataAll[originalLang][index].orig_mp4 || "");
            setVideo("sm");
        }
    }, [subtitlesDataAll[originalLang], folder, originalLang]);

    useEffect(() => {
        playingRef.current = playing;
        // Apply whatever the live stream produced while they were listening.
        // Deferring is only acceptable because it is picked up here; otherwise
        // new cues would wait for a page reload.
        if (!playing && pendingMp3Ref.current) {
            const next = pendingMp3Ref.current;
            pendingMp3Ref.current = null;
            setMp3(next);
        }
        if (!playing && pendingRealtimeReloadRef.current) {
            pendingRealtimeReloadRef.current = false;
            void getAllSubtitles();
        }
    }, [playing, getAllSubtitles]);

    const playToggle = useCallback(() => {
        if (playing) {
            setPlayingFragment(null);
        }
        if (!playing) {
            setPlaySavedTrack(false);
        }
        setPlaying(!playing);
    }, [playing]);

    useEffect(() => {
        if (mediaId && fastApiReady && fastApiMediaId === mediaId) {
            void getRusAudio(mediaId, folder);
        }
    }, [mediaId, folder, fastApiReady, fastApiMediaId]);

    useEffect(() => {
        if (!subtitlesRequest.hasData) {
            setTranslationTrackAll({});
            return;
        }
        if (subtitlesDataAll[originalLang] && mediaId && folder) {
            setTranslationTrackAll((prev) => {
                const newTracks = updateRusData() as AudioTrack[];
                const merged = prev[originalLang]
                    ? mergeTrackArrays(prev[originalLang]!, newTracks)
                    : newTracks;
                return { ...prev, [originalLang]: merged };
            });
            video && fetchVideoUrl();
        }
    }, [mediaId, subtitlesDataAll[originalLang], subtitlesDataAll[lang], folder, originalLang, lang, subtitlesRequest.hasData]);

    // The mode is a dependency: switching it (here, in another tab, by the build) re-lays the
    // open column out — drawn from the files with the mode on, resolved by the layout without.
    const manualOnlyLayout = manualOnlyControl.manualOnly;
    // id реплик оригинала открытой папки — по ним колонка озвучки узнаёт озвучку без реплики
    // оригинала (orphanVoicings.ts). Ключ — строка: правка текста оригинала не перекладывает
    // колонку озвучки, а удалённая или добавленная реплика — перекладывает. null — оригинал не
    // загружен или колонка и есть оригинал: сирот нет.
    const sourceCueIdsKey = useMemo(() => {
        if (lang === originalLang) return null;
        const rows = subtitlesDataAll[originalLang]?.[folder - 1]?.translations;
        return Array.isArray(rows) ? JSON.stringify(rows.map((row) => String(row.id))) : null;
    }, [subtitlesDataAll[originalLang], folder, lang, originalLang]);
    useEffect(() => {
        if (subtitlesRequest.hasData && subtitlesDataAll[lang] && mediaId && folder) {
            const sourceIds: string[] | null = sourceCueIdsKey === null ? null : JSON.parse(sourceCueIdsKey);
            setTranslationTrackAll((prev) => {
                const newTracks = prepareData(subtitlesDataAll[lang] || [], folder, manualOnlyLayout, sourceIds) as AudioTrack[];
                const merged = prev[lang]
                    ? mergeTrackArrays(prev[lang]!, newTracks)
                    : newTracks;
                return { ...prev, [lang]: merged };
            });
            setUpdatedTranslation((prev) => !prev);
        }
    }, [mediaId, subtitlesDataAll[lang], folder, lang, subtitlesRequest.hasData, manualOnlyLayout, sourceCueIdsKey]);

    const updatePosition = useCallback(
        async ({
            id,
            newPosition,
            newLength,
            tracks,
            editedLang,
        }: {
            id: string;
            newPosition: number;
            newLength: number;
            tracks: AudioTrack[];
            editedLang: ILang;
        }): Promise<boolean> => {
            // Испорченное время не уходит на сервер: он его не проверял и писал как есть
            // (конец −153841 у реплики fdcde2a1), а новый сервер откажет — и правка повиснет.
            if (!acceptTiming(editedLang, id, newPosition, newLength)) return false;
            if (liveTimingNow() && editedLang === originalLang) {
                // Живой эфир, колонка оригинала: время русской реплики — её слот, он и уходит; но
                // соседей по копии редактора не двигаем и не сохраняем (liveTiming.ts).
                saveOwnOnly(editedLang, folder, id, {
                    start: newPosition,
                    start_rus_milli: newPosition,
                    end_rus_milli: newLength + newPosition,
                    length: newLength,
                });
                return true;
            }
            if (liveTimingNow()) {
                // Живой эфир: только эта реплика и только то, что человек сдвинул или растянул
                // (liveTiming.ts, проба P50m10d) — соседей не раздвигаем и не сохраняем.
                const drawn = tracks.find((t) => t.original_id === id);
                const fields = liveTimingFields({
                    stored: storedRow(editedLang, folder, id),
                    drawn: drawn ? { start: drawn.start, length: drawn.length } : null,
                    position: newPosition,
                    length: newLength,
                });
                if (fields) saveLiveTiming(editedLang, folder, [{ trackId: id, fields }]);
                return true;
            }
            const patch: Partial<AudioTrackData> = {
                start: newPosition,
                start_rus_milli: newPosition,
                end_rus_milli: newLength + newPosition,
                length: newLength,
            };

            // Без автоправок своя правка — в несохранённые ДО раздвижки. Правка места снимает
            // прежний машинный сдвиг этой реплики (markDirty), а раздвижка метит сдвинутых внутри
            // обновления состояния, которое React может выполнить сразу: помеченная после, правка
            // сняла бы и только что сделанный сдвиг САМОЙ этой реплики (легла на предыдущую — её
            // сдвинули), и сервер получил бы место, которого на экране нет. Так последнее слово
            // за сдвигом: он уходит своим запросом после строки переводчика.
            // Без режима порядок прежний (правка — после раскладки): автоправка остаётся ровно
            // такой, как была, со всеми её особенностями.
            const ownFirst = manualOnlyRef.current;
            if (ownFirst) markDirty(editedLang, id, folder, patch);

            // Patch local state immediately and cascade overlap fixes
            setSubtitlesDataAll((prev) => {
                const updated = patchTrackInSubtitlesData(prev, editedLang, folder, id, patch);
                return cascadeNeighbours(updated, editedLang, folder);
            });

            // Mark dirty and schedule auto-save
            if (!ownFirst) markDirty(editedLang, id, folder, patch);
            scheduleAutoSave();
            return true;
        },
        [folder, mediaId, markDirty, scheduleAutoSave, cascadeNeighbours, acceptTiming, manualOnlyRef, liveTimingNow, storedRow, saveLiveTiming, saveOwnOnly, originalLang],
    );

    const updateTranslatedTrackPositions = useCallback(
        async ({
            diffPosition,
            selectedList,
        }: {
            diffPosition: number;
            selectedList: AudioTrack[];
        }) => {
            if (liveTimingNow()) {
                // Живой эфир: сдвигаются выделенные — от их места в данных, без раздвижки соседей
                // и без слота (liveTiming.ts, проба P50m10d).
                const shifts = selectedList
                    .map((track) => {
                        const base = storedRow(lang, folder, track.original_id) ?? track;
                        const fields = liveShiftFields(base, diffPosition);
                        return fields && acceptTiming(lang, track.original_id, fields.start as number, base.length)
                            ? { trackId: track.original_id, fields }
                            : null;
                    })
                    .filter((one): one is { trackId: string; fields: Partial<AudioTrackData> } => one !== null);
                if (shifts.length) saveLiveTiming(lang, folder, shifts);
                return undefined;
            }
            // Без автоправок сдвигается то, что лежит в файлах. Лента в этом режиме рисует файлы
            // как есть (prepareData), но `selectedList` — снимки, взятые при выделении: выделили
            // до включения режима — в снимке нарисованный раскладкой отступ, и сдвиг «от снимка»
            // записал бы этот машинный отступ в файл как работу переводчика.
            const stored = manualOnlyRef.current
                ? new Map(
                      (subtitlesDataAll[lang]?.[folder - 1]?.translations || []).map((t) => [t.id, t]),
                  )
                : null;
            // Build patches for all selected tracks
            const allPatches = selectedList.map((track) => {
                const base = stored?.get(track.original_id) ?? track;
                return {
                    trackId: track.original_id,
                    patch: {
                        start: base.start + diffPosition,
                        start_rus_milli: base.start + diffPosition,
                        end_rus_milli: base.start + base.length + diffPosition,
                    } as Partial<AudioTrackData>,
                };
            });
            // Озвучка раньше начала папки не уходит на сервер (место — единственное, что у
            // озвучки проверяется); остальные сдвигаются.
            const patches = allPatches.filter((p) =>
                acceptTiming(
                    lang,
                    p.trackId,
                    p.patch.start as number,
                    (p.patch.end_rus_milli as number) - (p.patch.start as number),
                ),
            );

            // Без автоправок сдвинутые человеком — в несохранённые ДО раздвижки (см.
            // updatePosition): если раздвижка сдвинула кого-то из них, сохраняется её место. Без
            // режима — прежний порядок.
            const markOwn = () => {
                for (const p of patches) {
                    markDirty(lang, p.trackId, folder, p.patch);
                }
            };
            const ownFirst = manualOnlyRef.current;
            if (ownFirst) markOwn();

            // Patch all local state at once and cascade overlap fixes
            setSubtitlesDataAll((prev) => {
                const updated = patchMultipleTracksInSubtitlesData(prev, lang, folder, patches);
                return cascadeNeighbours(updated, lang, folder);
            });

            // Mark all as dirty
            if (!ownFirst) markOwn();
            scheduleAutoSave();

            return undefined;
        },
        [translationTrackAll, lang, folder, markDirty, scheduleAutoSave, cascadeNeighbours, manualOnlyRef, subtitlesDataAll, acceptTiming, liveTimingNow, storedRow, saveLiveTiming],
    );

    const updateLength = useCallback(
        async ({
            id,
            newLength,
            start,
            editedLang,
        }: {
            id: string;
            newLength: number;
            start: number;
            editedLang: ILang;
        }): Promise<boolean> => {
            if (!acceptTiming(editedLang, id, start, newLength)) return false;
            const patch: Partial<AudioTrackData> = {
                start: start,
                start_rus_milli: start,
                end_rus_milli: newLength + start,
                length: newLength,
            };
            if (liveTimingNow()) {
                if (editedLang === originalLang) {
                    // Живой эфир, оригинал: слот реплики — её правка; соседей не двигаем (liveTiming.ts).
                    saveOwnOnly(editedLang, folder, id, patch);
                    return true;
                }
                // Живой эфир, перевод: только длина и место, если их изменил человек.
                const stored = storedRow(editedLang, folder, id);
                const fields = liveTimingFields({
                    stored,
                    drawn: stored ? { start: stored.start, length: stored.length } : null,
                    position: start,
                    length: newLength,
                });
                if (fields) saveLiveTiming(editedLang, folder, [{ trackId: id, fields }]);
                return true;
            }

            // Без автоправок своя правка — в несохранённые ДО раздвижки (см. updatePosition);
            // без режима — прежний порядок.
            const ownFirst = manualOnlyRef.current;
            if (ownFirst) markDirty(editedLang, id, folder, patch);

            // Patch local state immediately and cascade overlap fixes
            setSubtitlesDataAll((prev) => {
                const updated = patchTrackInSubtitlesData(prev, editedLang, folder, id, patch);
                return cascadeNeighbours(updated, editedLang, folder);
            });

            // Mark dirty and schedule auto-save
            if (!ownFirst) markDirty(editedLang, id, folder, patch);
            scheduleAutoSave();
            return true;
        },
        [folder, mediaId, markDirty, scheduleAutoSave, cascadeNeighbours, acceptTiming, manualOnlyRef, liveTimingNow, storedRow, saveLiveTiming, saveOwnOnly, originalLang],
    );

    // «Вернуть окно» чинит только испорченное — и только им. Реплика оригинала встаёт между
    // соседями, если сломано ЕЁ время; перевод получает окно реплики оригинала, если отрицательны
    // его место или слот (конец −153841 перевод получил копией), и законное место озвучки (≥ 0)
    // при этом остаётся. Целое не трогается: дубль, уложенный позже русского конца, с длиной ≤ 0
    // окна не лишён — его длину пересчитает переозвучка. Это явное действие человека, поэтому и
    // в режиме «Без автоправок» оно разрешено; соседей само не двигает (наезд, если он получится,
    // снимет следующая правка в папке — cascadeNeighbours).
    const restoreCueWindow = useCallback(
        (trackId: string): { start: number; end: number } | { error: string } => {
            const label = formatCueTime(
                cueStartMs(subtitlesDataAllRef.current, [originalLang, lang], folder, trackId),
            );
            const refuse = (error: string) => {
                setTranslatedOpError(`Окно не возвращено: реплика ${label} — ${error}`);
                return { error };
            };
            const tracks = translationTrackAll[originalLang] || [];
            const index = tracks.findIndex((track) => track.original_id === trackId);
            if (index < 0) return refuse("реплика не найдена на дорожке");
            const source = tracks[index];
            const sourceBroken = cueTimingBroken(source);
            const pairLang = lang !== originalLang ? lang : null;
            const target = pairLang
                ? subtitlesDataAllRef.current[pairLang]?.[folder - 1]?.translations.find((t) => t.id === trackId)
                : undefined;
            const targetBroken = Boolean(target && pairWindowBroken(pairTimingOf(target)));
            if (!sourceBroken && !targetBroken) {
                return refuse(
                    target && (target.timing_invalid === true || pairTimingProblem(target))
                        ? "окно реплики и перевода целое; длину озвучки пересчитает переозвучка (♪)"
                        : "время реплики и перевода целое — возвращать нечего",
                );
            }
            const gap = sourceBroken
                ? restoreWindow(tracks, index, durationFull)
                : cueWindow(source) || { error: "окно реплики оригинала не определить" };
            if ("error" in gap) return refuse(gap.error);

            const sourceTiming: Partial<AudioTrackData> = {
                start: gap.start,
                start_rus_milli: gap.start,
                end_rus_milli: gap.end,
                length: gap.end - gap.start,
            };
            let targetTiming: Partial<AudioTrackData> | null = null;
            let targetLocal: Partial<AudioTrackData> | null = null;
            if (targetBroken && target) {
                const placed = timeMs(target.start);
                const start = placed !== null && placed >= 0 ? placed : gap.start;
                // Место и слот уходят оба: сервер в обоих режимах запишет их и длину = конец − место.
                targetTiming = { start, start_rus_milli: start, end_rus_milli: gap.end };
                const repaired = { ...target, ...targetTiming, length: gap.end - start };
                const still = pairTimingProblem(repaired);
                targetLocal = {
                    ...targetTiming,
                    length: repaired.length,
                    timing_invalid: Boolean(still),
                    timing_problem: still || undefined,
                };
            }
            setSubtitlesDataAll((prev) => {
                let next = prev;
                if (sourceBroken) {
                    next = patchTrackInSubtitlesData(next, originalLang, folder, trackId, {
                        ...sourceTiming,
                        timing_invalid: false,
                        timing_problem: undefined,
                    });
                }
                if (targetLocal && pairLang) {
                    next = patchTrackInSubtitlesData(next, pairLang, folder, trackId, targetLocal);
                }
                return next;
            });
            if (sourceBroken) markDirty(originalLang, trackId, folder, sourceTiming);
            if (targetTiming && pairLang) markDirty(pairLang, trackId, folder, targetTiming);
            scheduleAutoSave();
            return gap;
        },
        [folder, lang, originalLang, translationTrackAll, durationFull, markDirty, scheduleAutoSave],
    );

    // Итог проверки короткой озвучки на слух в ответе ♪ (сервер слушает дубль текста до 3 слов и
    // переозвучивает до совпадения слов). Не совпало и после всех попыток — просьба к переводчику:
    // текст для красной полосы, с временем реплики, её текстом и тем, что слышно. Проверка
    // недоступна (ok: null, Scribe ответил ошибкой) — только запись в консоль; совпало — ничего.
    const voicingCheckFailureOf = useCallback(
        (response: unknown, folderNum: number, trackId: string): string | null => {
            const answer = (response || {}) as { voicing_check?: unknown; captions?: unknown };
            const check = voicingCheckOf(answer.voicing_check);
            if (!check || check.ok === true) return null;
            if (check.ok === null) {
                console.info("[♪] voicing_check unavailable — the take is kept unchecked", { trackId, folderNum, check });
                return null;
            }
            const data = subtitlesDataAllRef.current;
            // Текст — тот, что сервер озвучил (он в ответе ♪); нет — текст реплики в редакторе.
            const text =
                typeof answer.captions === "string" && answer.captions.trim()
                    ? answer.captions
                    : data[lang]?.[folderNum - 1]?.translations?.find((t) => t.id === trackId)?.captions;
            return voicingCheckFailure({
                at: formatCueTime(cueStartMs(data, [originalLang, lang], folderNum, trackId)),
                text,
                check,
            });
        },
        [lang, originalLang],
    );

    const audioSynthesize = useCallback(
        async ({
            trackId,
            folder: synthFolder,
            mediaId: synthMediaId,
            lang: synthLang,
            voice,
            speed,
            synt_audio_type,
            unskip,
            voiceNamed,
        }: {
            trackId: string;
            folder: number;
            mediaId: string;
            lang: MetadataToSubtitlesLanguages;
            voice: IVoiceType;
            speed: number;
            synt_audio_type?: "tts" | "original_ru";
            unskip?: boolean;
            voiceNamed?: boolean;
        }) => {
            // Flush gate: persist any pending changes first
            try {
                await flushGate();
            } catch (e) {
                // Причину (какая правка и почему не легла) полоса ошибок уже показывает — её
                // написало само сохранение; озвучка по устаревшему тексту не запускается.
                console.error("[SYNTH] flushGate FAILED - aborting synth to avoid using stale data:", e);
                return { error: "Failed to save pending changes before synthesis" };
            }

            setSavingStatus(true);
            setTranslatedSubtitleId(trackId);
            setEditedTrackId(synthFolder + "_" + trackId);

            // Режим — на момент запроса: по нему решено и `adjust: false`, и что делать с ответом.
            const manual = manualOnlyRef.current;
            // Снята ли реплика с озвучки до ♪: новый сервер вернёт её и пришлёт её текст (ниже).
            const wasSkipped =
                subtitlesDataAllRef.current[lang]?.[synthFolder - 1]?.translations?.find((t) => t.id === trackId)
                    ?.skip === true;
            let data: any;
            try {
                data = await syntAudioAction({
                    lang: synthLang,
                    folder_num: synthFolder,
                    metasubtitle_id: trackId,
                    mediaId: synthMediaId,
                    voice,
                    speed,
                    language_from: wireLang(originalLang),
                    synt_audio_type,
                    // Без автоправок сервер не подгоняет скорость и не раскладывает соседей (пара с
                    // флагом так делает и сама, это — явная просьба): ни к русскому началу, ни
                    // раньше. Наезд нового дубля на следующие новый сервер пары снимает и при
                    // adjust: false — сдвигом позже, по замеру wav, и называет толчки в ответе
                    // (`pushed`); у старого сервера наложения снимаются ниже, по перечитанной папке.
                    ...(manual ? { adjust: false } : {}),
                    // Снятую с озвучки реплику сервер озвучивает только по явной просьбе ♪ самой
                    // реплики; массовые проходы её не шлют, и снятие переводчика остаётся.
                    ...(unskip ? { unskip: true } : {}),
                    // Назвал ли голос человек: ♪ строки — нет (проба P50m10d), выбор голоса — да.
                    ...(voiceNamed !== undefined ? { voiceNamed } : {}),
                });
            } catch (e) {
                data = { error: errorHandler(e as Error) };
            }

            const failed = !data || (typeof data.error === "string" && !data.length);
            if (failed) {
                // Отказ — словами сервера и с временем реплики. Раньше не показывалось ничего,
                // а реплика ещё и попадала в «новые озвучки», будто всё получилось.
                const reason = data?.error || "сервер не ответил";
                setTranslatedOpError(
                    `Озвучка не создана: реплика ${formatCueTime(
                        cueStartMs(subtitlesDataAllRef.current, [originalLang, lang], synthFolder, trackId),
                    )} — ${reason}`,
                );
                setEditedTrackId(null);
                setTranslatedSubtitleId(null);
                setSavingStatus(false);
                return { error: reason };
            }

            // data now contains metadata with length, file, etc.
            {
                const meta = data as any;
                // Patch the synthesized track locally with new metadata
                const synthPatch: Partial<AudioTrackData> = {
                    ...(meta.length !== undefined ? { length: meta.length } : {}),
                    ...(meta.file !== undefined ? { file: meta.file } : {}),
                    ...(meta.voice !== undefined ? { voice: meta.voice } : {}),
                    ...(meta.speed !== undefined ? { speed: meta.speed } : {}),
                    ...(meta.start !== undefined ? { start: meta.start, start_rus_milli: meta.start } : {}),
                    ...(meta.shifted_start !== undefined ? { shifted_start: meta.shifted_start } : {}),
                    // Проверка на слух — про этот дубль. Нет её в ответе (длинный текст, старый
                    // сервер) — прежняя метка была про прежний дубль и снимается.
                    voicing_check: voicingCheckOf(meta.voicing_check),
                    ...skipAfterVoicing(meta, wasSkipped),
                };

                // Determine which ILang we're editing (the translated language)
                const editLang = lang !== originalLang ? lang : originalLang;

                const serverPushes = manual ? serverPushesOf(meta) : null;
                if (serverPushes) {
                    // Без автоправок, новый сервер (контракт C4): он уже отодвинул позже дубли, на
                    // которые налез новый (правило владельца 01.10.2026), меряя их по wav, и назвал
                    // каждый толчок в ответе — вместе с местом самой реплики ПОСЛЕ своего прохода и
                    // мерой нового дубля (`dub_ms`). Это ложится на экран сразу, ничего не
                    // пересчитывая и не сохраняя (всё записал сервер), и толчки называются.
                    // `start_rus_milli` толкнутых — как на диске: сервер его не двигает.
                    // Перечитывание папки остаётся подстраховкой: оно приносит то же, и раздвижка
                    // редактора после него не идёт — сервер прошёл папку целиком.
                    const pairLang = pairLangNow() ?? editLang;
                    const num = (value: unknown): value is number =>
                        typeof value === "number" && Number.isFinite(value);
                    const ownPatch: Partial<AudioTrackData> = {
                        ...(meta.length !== undefined ? { length: meta.length } : {}),
                        ...(meta.file !== undefined ? { file: meta.file } : {}),
                        ...(meta.voice !== undefined ? { voice: meta.voice } : {}),
                        ...(meta.speed !== undefined ? { speed: meta.speed } : {}),
                        ...(num(meta.start) ? { start: meta.start } : {}),
                        ...(num(meta.start_rus_milli) ? { start_rus_milli: meta.start_rus_milli } : {}),
                        ...(num(meta.shifted_start) ? { shifted_start: meta.shifted_start } : {}),
                        ...(num(meta.new_start) ? { new_start: meta.new_start } : {}),
                        dub_ms: num(meta.dub_ms) ? meta.dub_ms : meta.dub_ms === null ? null : undefined,
                        voicing_check: voicingCheckOf(meta.voicing_check),
                        ...skipAfterVoicing(meta, wasSkipped),
                    };
                    const before = subtitlesDataAllRef.current;
                    setSubtitlesDataAll((prev) =>
                        patchMultipleTracksInSubtitlesData(
                            patchTrackInSubtitlesData(prev, editLang, synthFolder, trackId, ownPatch),
                            pairLang,
                            synthFolder,
                            serverPushPatches(serverPushes),
                        ),
                    );
                    nameServerPushes(serverPushes, pairLang, synthFolder, before);
                    await getAllSubtitles();
                } else if (manual) {
                    // Без автоправок, старый сервер: он соседей не двигает. Отвечает он одной этой
                    // репликой, так что раздвижка по копии в редакторе и по json-длинам ставила бы
                    // соседку не там, где лежит её дубль. Поэтому: новый дубль — на экран (в
                    // несохранённые он не идёт: сервер его записал), папка — перечитать, и
                    // раздвигать только перечитанную (сдвигом позже). Не перечиталась — не
                    // раздвигаем по старой копии.
                    setSubtitlesDataAll((prev) =>
                        patchTrackInSubtitlesData(prev, editLang, synthFolder, trackId, synthPatch),
                    );
                    const reread = await getAllSubtitles();
                    pushOverlapsAfterServerVoicing(synthFolder, reread);
                } else if (liveStreamRef.current) {
                    // Живой эфир (проба 20261103-P50m10d, папка 1 en): раскладку папки делает конвейер
                    // эфира, а подгонку временем после правки — круг правки site_server (п.50г: она
                    // разрешена и называется в его отчёте). Раздвижка редактора здесь шла по его
                    // копии папки, а та отстаёт от сервера, пока идёт проход папки: ♪ в 21:07:30 —
                    // и 7 реплик, в том числе кусок фразы …211881, уехали за конец уже удалённого
                    // родителя (на 4170 мс от слова лектора), автосохранение записало это под именем
                    // переводчика в 21:08:14, строгий шаг остановил эфир. Поэтому на эфире ♪ кладёт
                    // на экран только то, что сервер записал о самой реплике; соседей не двигает и
                    // ничего, кроме неё, не сохраняет. Свежие места соседей приносит дозагрузка эфира.
                    setSubtitlesDataAll((prev) =>
                        patchTrackInSubtitlesData(prev, editLang, synthFolder, trackId, synthPatch),
                    );
                } else {
                    // Новый дубль самой реплики сервер уже записал — в несохранённые он не идёт;
                    // туда идут только сдвинутые раздвижкой.
                    setSubtitlesDataAll((prev) => {
                        // First patch the synthesized track
                        const updated = patchTrackInSubtitlesData(prev, editLang, synthFolder, trackId, synthPatch);

                        // Now run cascade on all tracks in this folder; shifted tracks are marked
                        // dirty for auto-save.
                        return cascadeNeighbours(updated, editLang, synthFolder);
                    });

                    // Schedule auto-save for any shifted tracks
                    if (dirtyTracksRef.current.size > 0) {
                        scheduleAutoSave();
                    }
                }
            }

            setChangedAudioTrackIds((prev) => [...(prev || []), synthFolder + "_" + trackId]);
            setEditedTrackId(null);
            setTranslatedSubtitleId(null);
            setSavingStatus(false);

            // Дубль есть и уложен; если сервер так и не услышал в нём текста — сказать об этом.
            const checkFailure = voicingCheckFailureOf(data, synthFolder, trackId);
            if (checkFailure) {
                setTranslatedOpError(checkFailure);
            }

            return data;
        },
        [flushGate, originalLang, lang, scheduleAutoSave, cascadeNeighbours, manualOnlyRef, getAllSubtitles, pushOverlapsAfterServerVoicing, pairLangNow, nameServerPushes, voicingCheckFailureOf],
    );

    const audioSynthesizeWithCheck = useCallback(
        async ({
            trackId,
            folder: synthFolder,
            mediaId: synthMediaId,
            lang: synthLang,
            voice,
            speed,
            unskip,
        }: {
            trackId: string;
            folder: number;
            mediaId: string;
            lang: MetadataToSubtitlesLanguages;
            voice: IVoiceType;
            speed: number;
            unskip?: boolean;
        }) => {
            const editLang = lang !== originalLang ? lang : originalLang;
            const synthPatch: Partial<AudioTrackData> = { voice, speed };

            setSubtitlesDataAll((prev) =>
                patchTrackInSubtitlesData(prev, editLang, synthFolder, trackId, synthPatch),
            );
            markDirty(editLang, trackId, synthFolder, synthPatch);

            try {
                await flushGate();
            } catch (e) {
                console.error("[VOICEOVER_V2] flushGate FAILED - aborting synth:", e);
                return { error: "Failed to save pending changes before checked synthesis" };
            }

            setSavingStatus(true);
            setTranslatedSubtitleId(trackId);
            setEditedTrackId(synthFolder + "_" + trackId);

            const at = () =>
                formatCueTime(cueStartMs(subtitlesDataAllRef.current, [originalLang, lang], synthFolder, trackId));
            try {
                const data = await processVoiceoverV2FragmentApi({
                    mediaId: synthMediaId,
                    folderNum: synthFolder,
                    languageFrom: wireLang(originalLang),
                    languageTo: synthLang,
                    metasubtitleId: trackId,
                    retryCount: 5,
                    mode: "autofix",
                    ...(unskip ? { unskip: true } : {}),
                });

                const reread = await getAllSubtitles();
                // Новый дубль положил сервер. Без автоправок новый сервер сам отодвигает налезшие
                // дубли позже (по замеру wav) и называет толчки в ответе — их называем и мы;
                // старый не двигает ничего — тогда наложения снимаются здесь, по перечитанной
                // папке, только сдвигом позже (afterServerVoicing).
                afterServerVoicing(synthFolder, reread, data);
                setChangedAudioTrackIds((prev) => [...(prev || []), synthFolder + "_" + trackId]);
                const problems: string[] = [];
                if (data && data.passed === false) {
                    // Озвучка есть, но проверку не прошла — это тоже надо видеть, а не узнавать
                    // на сборке.
                    const why = [
                        ...(Array.isArray(data.reason) ? data.reason : data.reason ? [data.reason] : []),
                        data.message,
                        data.last_error,
                    ]
                        .filter(Boolean)
                        .join("; ");
                    problems.push(`Озвучка реплики ${at()} не прошла проверку${why ? `: ${why}` : ""}`);
                }
                // Проверка короткой реплики на слух — как у ♪ без проверки.
                const checkFailure = voicingCheckFailureOf(data, synthFolder, trackId);
                if (checkFailure) {
                    problems.push(checkFailure);
                }
                if (problems.length) {
                    setTranslatedOpError(problems.join(". "));
                }
                return data;
            } catch (e) {
                // Раньше исключение уходило наружу: окно озвучки крутилось вечно, а у ♪
                // получался необработанный отказ — и ни слова о причине.
                const reason = errorHandler(e as Error);
                setTranslatedOpError(`Озвучка с проверкой не создана: реплика ${at()} — ${reason}`);
                return { error: reason };
            } finally {
                setEditedTrackId(null);
                setTranslatedSubtitleId(null);
                setSavingStatus(false);
            }
        },
        [flushGate, getAllSubtitles, lang, markDirty, originalLang, afterServerVoicing, voicingCheckFailureOf],
    );

    // Куски разбиения доделываются сами, по порядку: пустой — машинный перевод (как «→»; текст
    // переводчика не заменяется), потом озвучка тем голосом и скоростью, что достались от родителя
    // (как ♪). Отказ каждого шага провайдер называет в полосе ошибок; остальные куски идут дальше.
    completeSplitPiecesRef.current = async (ids: string[], targetLang: ILang) => {
        const pieceOf = (id: string) =>
            subtitlesDataAllRef.current[targetLang]?.[folder - 1]?.translations.find((t) => t.id === id);
        for (const id of ids) {
            let piece = pieceOf(id);
            if (!piece) {
                continue;
            }
            if (!(piece.captions || "").trim()) {
                const translated = await translateSubtitle({
                    toLang: targetLang,
                    fromLang: originalLang,
                    trackId: id,
                    quietIfExists: true,
                });
                if (translated.error) {
                    continue;
                }
                piece = pieceOf(id);
                if (!piece || !(piece.captions || "").trim()) {
                    continue;
                }
            }
            if (piece.file) {
                continue;
            }
            const args = {
                trackId: id,
                folder,
                mediaId,
                lang: MetadataToSubtitlesLanguages[targetLang],
                voice: (piece.voice || "male") as IVoiceType,
                // Голос куску достался от родителя — человек его не называл.
                voiceNamed: false,
                speed: Number(piece.speed) || 1,
            };
            try {
                if (useCheckedVoiceoverSynthesis) {
                    await audioSynthesizeWithCheck(args);
                } else {
                    await audioSynthesize(args);
                }
            } catch (e) {
                console.error("[split] voicing a piece failed:", e);
            }
        }
    };

    const cutAudioWithoutPause = useCallback(
        async ({ cut_snippets }: { cut_snippets: number[][] }) => {
            await flushGate();
            setSavingStatus(true);
            setSubtitleEditing(true);
            const data = await cutAudioWithoutPauseAction({
                mediaId,
                folder_num: folder,
                lang: wireLang(lang),
                cut_snippets,
            });
            await getAllSubtitles();
            setSavingStatus(false);
            setSubtitleEditing(false);
            return data;
        },
        [mediaId, folder, lang, getAllSubtitles, flushGate],
    );

    const addCatVideoFolderUrl = useCallback(({ folder, url }: { folder: number; url: string }) => {
        setCatVideoFolderUrl((prev) => ({ ...prev, [folder]: url }));
    }, []);

    const setLastActiveSubtitle = useCallback((data: { playOriginalStart: number; playbackTime: number }) => { lastActiveSubtitleRef.current = data; }, []);
    const setLastActiveVoiceover = useCallback((data: { trackId: string; playOriginalStart: number; playbackTime: number }) => { lastActiveVoiceoverRef.current = data; }, []);
    const getLastActiveSubtitle = useCallback(() => lastActiveSubtitleRef.current, []);
    const getLastActiveVoiceover = useCallback(() => lastActiveVoiceoverRef.current, []);

    const handlePause = () => {
        setPlayingFragmentNext(null);
        setPlayingFragment(null);
        setPlayOriginalStart(0);
        setPlaying(false);
    };

    const state = useMemo(() => {
        return {
            isDragging,
            zoomFragment,
            audioDuration,
            playing,
            voiceoverOnlyPlayback,
            playbackTime,
            activeSegmentLength,
            mediaId,
            mp3,
            translationTrackAll,
            subtitlesDataAll,
            subtitlesRequest,
            name,
            lang,
            toLang,
            video,
            playingFragment,
            playingFragmentRestart,
            playingFragmentNext,
            volumeOriginal,
            volumeTranslated,
            zoomLevel,
            changedRecently,
            changed,
            changedTranslation,
            changedRus,
            newData,
            scrubberWidth,
            playStart,
            playOriginalStart,
            folder,
            playbackRate,
            foldersTotal,
            waitingCut,
            editingSubtitles,
            scrollTo,
            videoUrl,
            lastFragmentTrimmed,
            subtitleMode,
            videoPlayerType,
            savingStatus,
            russian,
            trackSelected,
            translatedTrackSelected,
            translatedOpError,
            overlapPushNotice,
            focusCue,
            refusedEdits,
            durationFull,
            showOriginalLanguage,
            subtitleEditing,
            captionsSize,
            translatedSubtitleId,
            editedRecommendationSubtitle,
            startPlaying,
            voicingSpaces,
            voicingSpacesDeleted,
            voicingSpacesOn,
            voicingSpacesMinimum,
            voicingSpacesMinimumOffset,
            disableMovementRestrictions: disableMovementRestrictionsState,
            voicingSpacesAllPages,
            originalLang,
            catVideoFolderUrl,
            playSavedTrack,
            changedAudioTrackIds,
            updatedTranslation,
            editedTrackId,
            isDirty: dirtyTracks.size > 0,
            autoSaveStatus,
            selectedTranslatedTrackIds,
            selectedOriginalTrackIds,
            lectureName,
            lectureStatus,
            useCheckedVoiceoverSynthesis,
            muteOriginalOnVoicingClick,
            showGlobalTime,
            followPlayback,
            realtimeEditorStatus,
            manualOnly: manualOnlyControl.manualOnly,
            manualOnlyState: manualOnlyControl.state,
            manualOnlyApplicable: manualOnlyControl.applicable,
            manualOnlyLoading: manualOnlyControl.loading,
            manualOnlyError: manualOnlyControl.error,
            manualOnlyStream,
        };
    }, [
        isDragging,
        zoomFragment,
        audioDuration,
        playing,
        voiceoverOnlyPlayback,
        playbackTime,
        activeSegmentLength,
        mediaId,
        mp3,
        translationTrackAll,
        subtitlesDataAll,
        subtitlesRequest,
        name,
        lang,
        toLang,
        video,
        playingFragment,
        playingFragmentRestart,
        playingFragmentNext,
        volumeOriginal,
        volumeTranslated,
        zoomLevel,
        changedRecently,
        changed,
        changedTranslation,
        changedRus,
        newData,
        scrubberWidth,
        playStart,
        playOriginalStart,
        folder,
        playbackRate,
        foldersTotal,
        waitingCut,
        editingSubtitles,
        scrollTo,
        videoUrl,
        lastFragmentTrimmed,
        subtitleMode,
        videoPlayerType,
        savingStatus,
        russian,
        trackSelected,
        translatedTrackSelected,
        translatedOpError,
        overlapPushNotice,
        focusCue,
        refusedEdits,
        durationFull,
        showOriginalLanguage,
        subtitleEditing,
        captionsSize,
        translatedSubtitleId,
        editedRecommendationSubtitle,
        startPlaying,
        voicingSpaces,
        voicingSpacesDeleted,
        voicingSpacesOn,
        voicingSpacesMinimum,
        voicingSpacesMinimumOffset,
        disableMovementRestrictionsState,
        voicingSpacesAllPages,
        originalLang,
        catVideoFolderUrl,
        playSavedTrack,
        changedAudioTrackIds,
        updatedTranslation,
        editedTrackId,
        dirtyTracks,
        autoSaveStatus,
        selectedTranslatedTrackIds,
        selectedOriginalTrackIds,
        lectureName,
        lectureStatus,
        useCheckedVoiceoverSynthesis,
        realtimeEditorStatus,
        muteOriginalOnVoicingClick,
        showGlobalTime,
        // Без этой строки выключатель мёртв: объект состояния пересобирается только по
        // перечисленному здесь, и щелчок по нему не доходил бы до дорожек.
        followPlayback,
        manualOnlyControl.manualOnly,
        manualOnlyControl.state,
        manualOnlyControl.applicable,
        manualOnlyControl.loading,
        manualOnlyControl.error,
        manualOnlyStream,
    ]);

    // Manual trigger (lecture button, or a stream folder-wide override): regenerate translation
    // and TTS for the folder's edited Russian cues, with a full folder voiceover rebuild.
    // Scoped to the pair on screen — it used to fan out to every ru_* pair on the lecture, which
    // pushed a wording chosen for one language onto all the others. Returns immediately
    // (backend runs it in the background).
    const applyRuToAllLanguages = useCallback(async (targetFolder?: number) => {
        const folderNum = targetFolder ?? folder;
        const set = editedRuIdsByFolderRef.current[folderNum];
        const editedIds = set ? Array.from(set) : [];
        if (editedIds.length === 0) {
            return { status: "noop", reason: "no edited Russian cues in this folder" };
        }
        const targetLang = urlLang && urlLang !== originalLang ? urlLang : lang;
        const res = await applyRuCorrectionApi({
            mediaId,
            folderNum,
            editedIds,
            fullFolderRebuild: true,
            lang: targetLang,
            languageFrom: originalLang,
        });
        editedRuIdsByFolderRef.current[folderNum] = new Set();
        return res;
    }, [mediaId, folder, urlLang, lang, originalLang]);

    const handles = useMemo(() => {
        return {
            setIsDragging,
            getNextTrackId,
            setZoomFragment,
            setAudioDuration,
            setPlaying,
            setVoiceoverOnlyPlayback,
            setMuteOriginalOnVoicingClick,
            setShowGlobalTime,
            setFollowPlayback,
            setPlaybackTime,
            setActiveSegmentLength,
            setMediaId,
            setMp3,
            setTranslationTrackAll,
            setName,
            setLang,
            setToLang,
            setUrlLang,
            setVideo,
            setPlayingFragment,
            setPlayingFragmentRestart,
            setPlayingFragmentNext,
            playToggle,
            setVolumeOriginal,
            setVolumeTranslated,
            setZoomLevel,
            setNewData,
            setChangeToSave,
            setWaitingCut,
            setScrubberWidth,
            setPlayStart,
            setPlayOriginalStart,
            setFolder,
            setPlaybackRate,
            mergeCaptions,
            mergeTranslatedCaptions,
            splitTranslatedSubtitle,
            opusSplitTranslatedSubtitle,
            editMasterRuSubtitle,
            recreateFromRu,
            getRuDone,
            setRuDone,
            getTranslationDone,
            setTranslationDone,
            clearTranslatedOpError,
            clearOverlapPushNotice,
            showEditorError,
            clearFocusCue,
            discardRefusedEdits,
            unsavedTranslation,
            showSubtitleEditor,
            setChangedRecently,
            setScrollTo,
            scrollToElement,
            getElementById,
            deleteFragment,
            deleteSubtitle,
            setSubtitleMode,
            updatePosition,
            updateLength,
            restoreCueWindow,
            setVideoPlayerType,
            setRussian,
            translateSubtitle,
            setSavingStatus,
            audioSynthesize,
            audioSynthesizeWithCheck,
            setChangedTranslation,
            setChanged,
            setChangedRus,
            setChangedReels,
            fetchTracks: getAndSaveSubtitles,
            refreshAllSubtitles: getAllSubtitles,
            setShowOriginalLanguage,
            //
            editRusSubtitle,
            splitRusSubtitle,
            editTranslatedSubtitle,
            changeCaptionSize,
            setEditedRecommendationSubtitle,
            deleteAudio,
            setStartPlaying,
            createRusSubInterval,
            setVoicingSpacesDeleted,
            setVoicingSpacesOn,
            setVoicingSpacesMinimum,
            setVoicingSpacesMinimumOffset,
            setDisableMovementRestrictions,
            cutAudioWithoutPause,
            setOriginalLang,
            addCatVideoFolderUrl,
            updateTranslatedTrackPositions,
            deleteTranslatedSubtitle,
            deleteOrphanVoicing,
            handlePause,
            setPlaySavedTrack,
            setTime,
            deleteSubtitles,
            createRusSubIntervalMulti,
            flushDirtyChanges,
            refreshRealtimeEditorStatus,
            flushGate,
            applyRuToAllLanguages,
            setLastActiveSubtitle,
            setLastActiveVoiceover,
            getLastActiveSubtitle,
            getLastActiveVoiceover,
            setSelectedTranslatedTrackIds,
            setSelectedOriginalTrackIds,
            setUseCheckedVoiceoverSynthesis,
            setManualOnly: manualOnlyControl.setManualOnly,
            refreshManualOnly: manualOnlyControl.refresh,
        };
    }, [
        editRusSubtitle,
        setAudioDuration,
        splitRusSubtitle,
        applyRuToAllLanguages,
        getAndSaveSubtitles,
        getAllSubtitles,
        playToggle,
        mergeCaptions,
        mergeTranslatedCaptions,
        splitTranslatedSubtitle,
        opusSplitTranslatedSubtitle,
        editMasterRuSubtitle,
        recreateFromRu,
        getRuDone,
        setRuDone,
        getTranslationDone,
        setTranslationDone,
        clearTranslatedOpError,
        clearOverlapPushNotice,
        showEditorError,
        clearFocusCue,
        discardRefusedEdits,
        unsavedTranslation,
        showSubtitleEditor,
        scrollToElement,
        getElementById,
        deleteSubtitle,
        updatePosition,
        updateLength,
        restoreCueWindow,
        translateSubtitle,
        audioSynthesize,
        audioSynthesizeWithCheck,
        editTranslatedSubtitle,
        deleteAudio,
        setStartPlaying,
        createRusSubInterval,
        setVoicingSpacesDeleted,
        setVoicingSpacesOn,
        deleteSubtitles,
        createRusSubIntervalMulti,
        setVoicingSpacesMinimum,
        setVoicingSpacesMinimumOffset,
        setDisableMovementRestrictions,
        cutAudioWithoutPause,
        setOriginalLang,
        addCatVideoFolderUrl,
        updateTranslatedTrackPositions,
        deleteOrphanVoicing,
        handlePause,
        setPlaySavedTrack,
        deleteSubtitles,
        createRusSubIntervalMulti,
        flushDirtyChanges,
        refreshRealtimeEditorStatus,
        flushGate,
        setUseCheckedVoiceoverSynthesis,
        manualOnlyControl.setManualOnly,
        manualOnlyControl.refresh,
    ]);

    return (
        <>
            <AudioContextState.Provider value={state}>
                <AudioContextHandles.Provider value={handles}>
                    {children}
                </AudioContextHandles.Provider>
            </AudioContextState.Provider>
        </>
    );
};

export { AudioContextState, AudioProvider, AudioContextHandles };
